// Portagem fiel do ControleFC/frontend/src/pages/NotasEntrada/NotaEntradaImportar.jsx (~1825 linhas).
// Adaptacoes mínimas:
// - api: prefixos /api/ + opts.query (em vez de params)
// - parse XML: le arquivo como texto e manda { xml: '...' } (sem multipart)
// - configuracoes/fiscais → /api/configuracoes/fiscal
// - modal "Gerar Contas a Pagar" gera contas via /api/contas-pagar/importar-nota/:id (Onda 3.3c-CP)
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { upperMantendoCursor } from '../../components/inputs/UpperInput';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, FileText, CheckCircle, AlertTriangle, Package, Plus, X, Loader2, Search, Link2 } from 'lucide-react';
import { api } from '../../lib/api';
import { useDialog } from '../../components/DialogProvider';
import { MSG_PRODUTO_INATIVO, produtoInativo, SeloInativo } from '../../lib/produto-inativo';
import { formatMoeda, formatMoedaSemSimbolo, contar, itensSemPrateleira, rotulosSemPrateleira, embalagemDaLinhaNovaPelaNota, precoDaEmbalagemPelaNota, estoqueNaEmbalagemDaLinha, cfopProdutoNovo, embalagemAdotadaPelaLinha, cadastroContaUnidadesDoEstoque } from '@fiodecobre/shared';
import { ComboBox, type OpcaoCombo } from '../../components/ComboBox';
import { ModalCadastroRapido } from '../../components/ModalCadastroRapido';
import { ModalEmbalagemCompra } from '../cotacoes/ModalEmbalagemCompra';
// ⚠️ Esta tela ainda desenha as OUTRAS miniaturas com `<img>` cru (divida das 19 telas descrita no
// sistema/CLAUDE.md). A secao nova nasce certa: `contain` + fundo branco em par, senao produto
// comprido perde o rotulo que o identifica.
import { FotoProduto } from '../../components/FotoProduto';

// `ativo` (migration 0311): inativa sai das listas de escolha, mas segue achavel por id/sigla.
interface Unidade { id: number; nome: string; sigla: string; ativo?: boolean }
interface LugarProduto {
  localizacao_id: number;
  codigo: string;
  descricao: string | null;
  principal: boolean;
  quantidade: string | null;
}
interface ItemNF {
  numero_item: number;
  codigo_fornecedor: string;
  ean: string;
  descricao: string;
  ncm: string;
  cest: string;
  cfop: string;
  origem: string;
  unidade: string;
  quantidade: number;
  valor_unitario: number;
  valor_total: number;
  cst_icms: string;
  cst_pis: string;
  cst_cofins: string;
  aliquota_icms: number;
  aliquota_ipi: number;
  aliquota_pis: number;
  aliquota_cofins: number;
  valor_icms: number;
  valor_ipi: number;
  valor_pis: number;
  valor_cofins: number;
  valor_bc_st: number;
  valor_icms_st: number;
  info_adicional: string;
  marca_nf?: string;
}
interface NfeData {
  chave_acesso: string;
  numero: string;
  serie: string;
  data_emissao: string | null;
  natureza_operacao: string;
  emitente: { cnpj: string; nome: string; ie: string; endereco: { logradouro: string; numero: string; bairro: string; municipio: string; codigo_ibge_cidade?: string; uf: string; cep: string; fone: string } };
  valor_produtos: number;
  valor_frete: number;
  valor_desconto: number;
  valor_total: number;
  transportadora: string;
  peso_bruto: number;
  volumes: number;
  informacoes_complementares: string;
  duplicatas: Array<{ numero: string; vencimento: string; valor: string }>;
  itens: ItemNF[];
  // Backend popula esse campo no /parse quando a chave_acesso ja existe no banco.
  // Frontend usa pra avisar o operador imediatamente via dialog.alerta.
  ja_importada?: { id: number; criado_em: string } | null;
  // Campos manuais da ultima importacao (sobrevivem ao DELETE da nota).
  memorial?: {
    outras_despesas: number;
    credito_fornecedor: number;
    atualizado_em: string;
    atualizado_por_nome: string | null;
  } | null;
}
/** Item da prateleira NOVOS PRODUTOS (DA-606) — `GET /api/novos-produtos/busca`. */
interface NovoProdutoBusca {
  id: number;
  codigo: string;
  descricao: string;
  marca: string | null;
  unidade_sigla: string | null;
  embalagem: string | null;
  ncm: string | null;
  foto_url: string | null;
}

// Cotacao de origem (migration 0356): as do fornecedor da nota e os itens da escolhida.
interface CotacaoDoFornecedor {
  id: number;
  numero: string;
  observacoes: string | null;
  status: string;
  data_abertura: string | null;
  itens: number;
  marcados_aqui: number;
}
interface ItemCotacaoNota {
  cotacao_item_id: number;
  tipo: 'produto' | 'novo' | 'catalogo';
  descricao: string | null;
  cotado: string;
  cod_fornecedor: string | null;
  marcado_aqui: boolean;
  produto: ProdutoVinc | null;
  novo_produto: NovoProdutoBusca | null;
  novo_produto_status: string | null;
}

interface ProdutoVinc {
  id: number;
  codigo_interno: string | null;
  descricao: string;
  marca_nome: string | null;
  // A marca do CADASTRO do produto. Le-se `marca_nome` pra exibir e `marca_id` pra decidir se o
  // cadastro ja sabe a marca — nome vazio e nome ausente sao a mesma coisa aqui, mas so o id
  // separa "o produto nao tem marca" de "a rota nao trouxe o campo".
  marca_id?: number | null;
  ean: string | null;
  estoque_atual: string | number;
  foto_url: string | null;
  unidade_venda_id: number | null;
  unidade_sigla: string | null;
  fator_conversao: string | null;
  unidade_compra: string | null;
  qtd_embalagem_padrao: string | null;
  unidade_conteudo_embalagem: string | null;
  preco_custo: string | number;
  // Uso/Consumo do proprio produto (item de consumo da loja, escondido da venda).
  // POST /api/produtos e a busca ja devolvem essa flag — usada pra manter o selo
  // visivel na linha DEPOIS de criar/vincular (senao o operador perde a confirmacao).
  uso_consumo?: boolean;
  status?: string | null;
}
interface ItemVinculado extends ItemNF {
  produto_id: number | null;
  produto_vinculado: ProdutoVinc | null;
  emb_venda_id: number | null;
  emb_venda_sigla: string;
  qtd_embalagem: number;
  _qtd_emb_str?: string;
  st_calculado?: boolean;
  // Prateleira que vai receber a mercadoria ([DA-448]). Nasce com a sugestao do backend (o mesmo
  // lugar que a cascata escolheria sozinha) e o operador ajusta onde discorda.
  localizacao_id?: number | null;
  // Os lugares que o produto JA tem, so pra a dica da celula dizer se e' a vitrine ou o deposito.
  _lugares?: LugarProduto[];
  // Uso/Consumo: quando o operador marca esta linha, o produto CRIADO por ela nasce
  // com uso_consumo=true (nao aparece na venda). So afeta a CRIACAO — vincular a um
  // produto existente nao mexe na flag dele.
  uso_consumo?: boolean;
  // A linha da prateleira NOVOS PRODUTOS que esta linha do XML representa (DA-606). Enquanto o
  // item nao tem produto do cadastro, e por aqui que a importacao sabe QUEM aposentar: no salvar,
  // o Novo Produto vira `comprado` e a identidade migra pro Catalogo do fornecedor DESTA nota.
  // ⚠️ O casamento e do OPERADOR, nunca do sistema: nada acontece calado por parecenca de texto.
  novo_produto_id?: number | null;
  novo_produto_codigo?: string | null;
  // Marca escolhida na linha "criar uma linha nova para esta compra". Fica FORA de `marca_nf`, que
  // e' o que o XML trouxe e nao pode ser sobrescrito: a coluna do item mostra o XML, e quem edita
  // cadastro e' a linha do catalogo. So esta viaja como marca da linha que a importacao cria.
  _marca_nova?: string;
  // Marca escolhida na coluna do ITEM, que grava no CADASTRO do produto (05/09/2026). Destino
  // diferente do `_marca_nova` acima, que e' do catalogo do fornecedor — sao dois campos
  // independentes, e essa independencia e' o que separa isto do espelho de mao dupla que ele
  // cortou na [DA-600] p.2.
  // `undefined` = ninguem digitou, a celula ESPELHA (a marca do cadastro quando ela existe; a do
  // catalogo em rascunho quando o cadastro esta vazio). String = o operador mandou, e manda.
  _marca_produto?: string;
  // Embalagem e quantidade da linha que a importacao vai CRIAR no catalogo — os mesmos dois campos
  // do modal "Editar item do catalogo". Ficam `undefined` enquanto ninguem digita, e nesse estado a
  // celula MOSTRA o que a nota traz (a unidade comercial e o Qt.Emb da linha): a linha nova nascia
  // com as duas celulas em branco, entao nao havia como saber com que embalagem o registro ia
  // nascer — nem corrigi-la. Digitou, manda; nao digitou, segue o de sempre.
  _cat_emb_nova?: string;
  _cat_qtd_nova?: string;
}
// O que vai acontecer com este item DENTRO do catalogo do fornecedor (DA-598). Vem inteiro do
// backend: a tela nao recalcula nada: previa que decide por conta propria vira conferencia que
// mente, e conferencia que mente e pior que nao existir.
interface CandidatoCatalogo {
  id: number;
  codigo_fornecedor: string | null;
  codigo_fornecedor_alt: string | null;
  descricao_fornecedor: string;
  marca: string | null;
  embalagem: string | null;
  quantidade_embalagem: number | null;
  preco_unitario: number | null;
  /** O mesmo item noutro CNPJ da empresa, numa embalagem com Qtd > 1 (a linha em unidade adota). */
  irma_embalagem?: string | null;
  irma_quantidade?: number | null;
  tem_alt: boolean;
  /** e' esta a linha que o codigo do XML alcanca hoje */
  casa_por_codigo: boolean;
}
interface DestinoCatalogo {
  produto_id: number;
  item_casado_id: number | null;
  /** TODAS as linhas que o fornecedor tem pro nosso codigo interno — o operador marca a da compra */
  linhas: CandidatoCatalogo[];
  situacao: 'atualiza' | 'cria' | 'decidir';
}
// `id` e a EMPRESA (MARTINS). `filial_id` e a INSCRICAO que emitiu a nota (MARTINS MG) — e dela que
// vem o CNPJ, a UF do DIFAL e o CNPJ que emite o boleto do Contas a Pagar (DA-311).
interface Fornecedor {
  id: number;
  razao_social: string;
  nome_fantasia: string | null;
  cpf_cnpj: string | null;
  cidade?: string | null;
  uf?: string | null;
  filial_id?: number | null;
  filial_apelido?: string | null;
}
interface ConfigFiscal {
  fiscal_aliq_interna_efetiva: string;
  fiscal_usar_beneficio: string;
  fiscal_aliq_interna_nominal: string;
  fiscal_aliq_inter_sul_sudeste: string;
  fiscal_aliq_inter_outros: string;
  fiscal_aliq_inter_importados: string;
  fiscal_ufs_aliq_7: string;
}

const UNIDADES_FIXAS: Record<string, number> = {
  UN: 1, U: 1, PC: 1, PCA: 1,
  DZ: 12, DUZ: 12,
  CE: 100, CEN: 100, CTN: 100, CT: 100,
  MIL: 1000, MI: 1000, ML: 1000, MLR: 1000,
};

// Valida o digito verificador (cDV) da chave NF-e de 44 digitos usando modulo 11.
// Pesos ciclicos 2..9 da direita pra esquerda sobre os 43 primeiros digitos.
function dvChaveValido(chave: string): boolean {
  if (!/^\d{44}$/.test(chave)) return false;
  let soma = 0;
  let peso = 2;
  for (let i = 42; i >= 0; i--) {
    soma += Number(chave[i]) * peso;
    peso = peso === 9 ? 2 : peso + 1;
  }
  const r = soma % 11;
  const dv = r === 0 || r === 1 ? 0 : 11 - r;
  return dv === Number(chave[43]);
}

export function NotaEntradaImportar() {
  const navigate = useNavigate();
  const dialog = useDialog();
  const [dragging, setDragging] = useState(false);
  const [unidades, setUnidades] = useState<Unidade[]>([]);
  // Marca visual nas linhas pra nao perder a referencia entre cliques em inputs.
  // Click numa area "vazia" da linha (fora de inputs/selects/botoes) toggle a marca.
  const [linhasMarcadas, setLinhasMarcadas] = useState<Set<number>>(() => new Set());

  // Cadastro inteiro de prateleiras — o combo da coluna Localizacao oferece TODAS, nao so as que o
  // produto ja tem ([DA-448]): produto sem endereco nenhum e' justamente quem mais precisa escolher.
  const [opcoesLocal, setOpcoesLocal] = useState<OpcaoCombo[]>([]);

  useEffect(() => {
    api.get<Unidade[]>('/api/unidades-medida').then(d => setUnidades(Array.isArray(d) ? d : [])).catch(() => {});
    api.get<{ id: number; codigo: string }[]>('/api/localizacoes')
      .then(l => setOpcoesLocal((Array.isArray(l) ? l : []).map(x => ({ value: x.id, label: x.codigo }))))
      .catch(() => {});
    api.get<{ id: number; nome: string }[]>('/api/marcas')
      .then(m => setMarcas(Array.isArray(m) ? m : [])).catch(() => {});
  }, []);

  // Pergunta ao backend, de uma vez pra todos os produtos vinculados, quais lugares eles tem e qual
  // a tela ja deixa escolhido. Best-effort: se falhar, a coluna nasce vazia e o backend cai no
  // caminho de sempre (a cascata) — a importacao nao pode quebrar por causa de uma sugestao.
  const buscarLugares = useCallback(async (ids: number[]) => {
    if (ids.length === 0) return {};
    try {
      return await api.post<Record<string, { lugares: LugarProduto[]; sugerida_id: number | null }>>(
        '/api/notas-entrada/localizacoes-sugeridas', { produto_ids: ids },
      );
    } catch { return {}; }
  }, []);

  const aplicarSugestoesLocal = useCallback(async (lista: ItemVinculado[]): Promise<ItemVinculado[]> => {
    const ids = [...new Set(lista.map(i => i.produto_id).filter((n): n is number => !!n))];
    if (ids.length === 0) return lista;
    const mapa = await buscarLugares(ids);
    if (Object.keys(mapa).length === 0) return lista;
    return lista.map(item => {
      if (!item.produto_id) return { ...item, localizacao_id: null, _lugares: [] };
      const info = mapa[String(item.produto_id)];
      return { ...item, localizacao_id: info?.sugerida_id ?? null, _lugares: info?.lugares ?? [] };
    });
  }, [buscarLugares]);

  // Trocar o PRODUTO de uma linha invalida o lugar sugerido pra ela — o lugar era do produto
  // anterior. Sem isso a coluna continuaria mostrando a prateleira de outra mercadoria, que e' pior
  // que mostrar vazio: o operador confirmaria sem desconfiar. Roda por linha, depois do vinculo.
  const recarregarLocalDaLinha = useCallback(async (idx: number, produtoId: number | null) => {
    if (!produtoId) {
      setItensVinculados(prev => prev.map((it, i) => i === idx ? { ...it, localizacao_id: null, _lugares: [] } : it));
      return;
    }
    const mapa = await buscarLugares([produtoId]);
    const info = mapa[String(produtoId)];
    setItensVinculados(prev => prev.map((it, i) => i === idx
      ? { ...it, localizacao_id: info?.sugerida_id ?? null, _lugares: info?.lugares ?? [] }
      : it));
  }, [buscarLugares]);

  const [loading, setLoading] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState('');

  const [nfe, setNfe] = useState<NfeData | null>(null);
  const [stEmbutido, setStEmbutido] = useState(false);
  const [fornecedorVinculado, setFornecedorVinculado] = useState<Fornecedor | null>(null);
  const [itensVinculados, setItensVinculados] = useState<ItemVinculado[]>([]);
  const [atualizarEstoque, setAtualizarEstoque] = useState(true);
  // O destino de cada item no catalogo, paralelo a `itensVinculados` pelo INDICE (dois itens da
  // nota podem apontar pro mesmo produto, entao a chave nao pode ser o produto).
  const [destinosCat, setDestinosCat] = useState<Array<DestinoCatalogo | null>>([]);
  // A escolha do operador na linha do catalogo: id do item com que casar, ou 'novo'. Fica de fora
  // do `itensVinculados` de proposito — ela nao descreve a nota, e sim o que fazer com o cadastro.
  const [escolhaCat, setEscolhaCat] = useState<Record<number, number | 'novo'>>({});
  // Marca corrigida direto na linha do CATALOGO, por id do item do catalogo. Serve pra linha que
  // esta com a marca errada e NAO e a que vai receber esta compra — o campo do item so alcanca a
  // marcada. Como tudo nesta tela, so grava quando o operador confirma a importacao.
  const [marcaCat, setMarcaCat] = useState<Record<number, string>>({});
  // Embalagem/quantidade corrigidas na linha EXISTENTE do catalogo que vai receber a compra, por
  // indice do item da nota (pedido dele, 29/10/2026: "o sistema nao permite alterar a unidade e a
  // quantidade"). `linha` amarra a edicao a linha marcada: trocou a marcacao, a edicao deixa de valer.
  const [pernaCat, setPernaCat] = useState<Record<number, { linha: number; emb?: string; qtd?: string }>>({});
  // Mesmo par do Catalogo (CatalogoFornecedor): combo das marcas cadastradas + "+" que abre o
  // cadastro rapido. `marcaNovaAlvo` guarda ONDE devolver a marca recem-criada — a linha do
  // catalogo (id), a linha nova de um item (idx) ou o CADASTRO do produto daquele item
  // (`produto: true`, a coluna Marca da linha de cima — 05/09/2026).
  const [marcas, setMarcas] = useState<{ id: number; nome: string }[]>([]);
  const [marcaNovaAlvo, setMarcaNovaAlvo] = useState<{ idx: number; idLinha: number | null; produto?: boolean } | null>(null);
  const reqCatRef = useRef(0);
  // A conferencia do catalogo FALHA ABERTA de proposito: nao da pra impedir a nota de entrar por
  // causa de uma checagem de cadastro. Mas ela nao pode falhar CALADA — sem este estado a celula
  // ficaria em "conferindo..." pra sempre, que o operador le como "esta tudo certo".
  const [erroPreviaCat, setErroPreviaCat] = useState(false);
  const [totalNotaFiscal, setTotalNotaFiscal] = useState('');
  const [outrasDespesas, setOutrasDespesas] = useState('');
  // Credito de fidelidade do fornecedor — abate so o CP, NAO o custo dos produtos
  // (ver memoria project_credito_fornecedor_so_no_titulo)
  const [creditoFornecedor, setCreditoFornecedor] = useState('');

  // Modal consultar (stub SEFAZ)
  const [modalConsultar, setModalConsultar] = useState(false);
  const [chaveConsulta, setChaveConsulta] = useState('');
  const [consultando, setConsultando] = useState(false);

  // Modal cadastrar fornecedor
  const [modalFornecedor, setModalFornecedor] = useState(false);
  const [salvandoFornecedor, setSalvandoFornecedor] = useState(false);

  // Modal vincular fornecedor
  const [modalVincularForn, setModalVincularForn] = useState(false);
  const [buscaForn, setBuscaForn] = useState('');
  const [resultadosForn, setResultadosForn] = useState<Fornecedor[]>([]);
  const [buscandoForn, setBuscandoForn] = useState(false);

  useEffect(() => {
    if (!modalVincularForn) return;
    const q = buscaForn.trim();
    if (q.length < 2) { setResultadosForn([]); return; }
    setBuscandoForn(true);
    const timer = setTimeout(async () => {
      try {
        const data = await api.get<Fornecedor[]>('/api/fornecedores', { query: { busca: q } });
        setResultadosForn(Array.isArray(data) ? data : []);
      } catch { setResultadosForn([]); }
      setBuscandoForn(false);
    }, 400);
    return () => { clearTimeout(timer); setBuscandoForn(false); };
  }, [buscaForn, modalVincularForn]);

  // Modal buscar produto (lupa)
  const [modalBuscaProd, setModalBuscaProd] = useState<{ idx: number } | null>(null);
  const [buscaProd, setBuscaProd] = useState('');
  const buscaProdInputRef = useRef<HTMLInputElement | null>(null);
  const buscaProdSelRef = useRef<{ start: number | null; end: number | null } | null>(null);
  const [resultadosProd, setResultadosProd] = useState<ProdutoVinc[]>([]);
  // A segunda secao do modal da lupa: a prateleira NOVOS PRODUTOS (DA-606). Vem por ULTIMO, depois
  // do casamento normal — regra do Ruan: "Primeiro pesquisa normal e por ultimo em Novos Produto".
  const [resultadosNovos, setResultadosNovos] = useState<NovoProdutoBusca[]>([]);
  const [buscandoProd, setBuscandoProd] = useState(false);

  // Cotacao de ORIGEM (migration 0356, pedido do Ruan 31/10/2026) — OPCIONAL, nasce vazia. Escolhida,
  // a lupa abre com os itens cotados no topo (produto e Novo Produto) e cada linha mostra o que foi
  // pedido ("Cotado: 200 UN (2 CX)"). O casamento segue do operador ([DA-606]): nada se liga sozinho.
  const [cotacoesForn, setCotacoesForn] = useState<CotacaoDoFornecedor[]>([]);
  const [cotacaoOrigemId, setCotacaoOrigemId] = useState<number | null>(null);
  const [itensCotacao, setItensCotacao] = useState<ItemCotacaoNota[]>([]);
  const [erroCotacao, setErroCotacao] = useState('');

  useEffect(() => {
    setCotacaoOrigemId(null);
    setCotacoesForn([]);
    const fid = fornecedorVinculado?.id;
    if (!fid) return;
    let vivo = true;
    api.get<CotacaoDoFornecedor[]>(`/api/notas-entrada/cotacoes-do-fornecedor/${fid}`)
      .then(r => { if (vivo) setCotacoesForn(Array.isArray(r) ? r : []); })
      .catch(() => { if (vivo) setCotacoesForn([]); });
    return () => { vivo = false; };
  }, [fornecedorVinculado?.id]);

  useEffect(() => {
    setItensCotacao([]);
    setErroCotacao('');
    if (!cotacaoOrigemId || !fornecedorVinculado?.id) return;
    let vivo = true;
    api.get<ItemCotacaoNota[]>(`/api/notas-entrada/cotacao-para-nota/${cotacaoOrigemId}`, { query: { fornecedor_id: String(fornecedorVinculado.id) } })
      .then(r => { if (vivo) setItensCotacao(Array.isArray(r) ? r : []); })
      .catch((e: any) => { if (vivo) setErroCotacao(e?.message || 'Nao consegui ler os itens da cotacao'); });
    return () => { vivo = false; };
  }, [cotacaoOrigemId, fornecedorVinculado?.id]);

  useEffect(() => {
    if (buscaProdSelRef.current && buscaProdInputRef.current) {
      const { start, end } = buscaProdSelRef.current;
      buscaProdInputRef.current.setSelectionRange(start, end);
      buscaProdSelRef.current = null;
    }
  }, [buscaProd]);

  useEffect(() => {
    if (!modalBuscaProd) return;
    const q = buscaProd.trim();
    if (q.length < 2) { setResultadosProd([]); setResultadosNovos([]); return; }
    setBuscandoProd(true);
    const timer = setTimeout(async () => {
      try {
        const [data, novos] = await Promise.all([
          api.get<ProdutoVinc[]>('/api/produtos', { query: { q, com_inativos: '1' } }),
          api.get<NovoProdutoBusca[]>('/api/novos-produtos/busca', { query: { q } })
            .catch(() => [] as NovoProdutoBusca[]),
        ]);
        setResultadosProd(Array.isArray(data) ? data.slice(0, 20) : []);
        setResultadosNovos(Array.isArray(novos) ? novos.slice(0, 20) : []);
      } catch { setResultadosProd([]); setResultadosNovos([]); }
      setBuscandoProd(false);
    }, 400);
    return () => { clearTimeout(timer); setBuscandoProd(false); };
  }, [buscaProd, modalBuscaProd]);

  // Modal criar produto
  const [modalProduto, setModalProduto] = useState<{ idx: number; item: ItemNF } | null>(null);
  const [salvandoProduto, setSalvandoProduto] = useState(false);
  // Como o produto NOVO e vendido: quantos, e em que unidade, cabem em 1 embalagem da nota. Ate
  // 22/08/2026 o modal era so-leitura e nao havia onde dizer isto: o produto nascia com a
  // `unidade_compra` do XML, `qtd_embalagem_padrao` VAZIA e vendido na propria sigla do fornecedor
  // (CD, PT, PCA). Consequencia medida em prod: 92 de 178 produtos com unidade de compra ficaram
  // sem Qtd na Emb., a linha da nota entrava com Qt.Emb 1 e o catalogo gravava o preco da
  // embalagem inteira como unitario (o cento de parafuso a R$ 32,25 "cada").
  const [novoQtdEmb, setNovoQtdEmb] = useState('1');
  const [novoUnVendaId, setNovoUnVendaId] = useState<number | null>(null);
  // Como o produto NOVO e COMPRADO (regra dele, 30/10/2026: *"o xml tem que solicitar a Qtde de
  // Compra"*). Nasce igual a nota (sigla do XML, a mesma Qtd de cima) e so diverge quando ele muda:
  // o conector 1035 vem em UN no XML, mas a compra e' o PCT com 10. `null` = segue a nota.
  const [novoCompraUn, setNovoCompraUn] = useState<string | null>(null);
  const [novoCompraQtd, setNovoCompraQtd] = useState<string | null>(null);
  // Produto JA cadastrado sem Unidade de Compra: a linha pede, pela mesma janelinha da cotacao
  // ([DA-856]), que grava SO os tres campos da compra na ficha.
  const [modalEmbCompra, setModalEmbCompra] = useState<{ produtoId: number; codigo: string | null; descricao: string | null } | null>(null);

  // Modal gerar contas a pagar — gera no sistema novo (Onda 3.3c-CP)
  const [modalGerarContas, setModalGerarContas] = useState<{
    notaId: number;
    duplicatas: Array<{ numero: string; vencimento: string; valor: string }>;
    valorTotalNF: number;        // vNF (cheio)
    creditoFornecedor: number;
    outrasDespesas: number;
  } | null>(null);
  const [categoriasDespesa, setCategoriasDespesa] = useState<Array<{ id: number; nome: string }>>([]);
  const [categoriaContas, setCategoriaContas] = useState<string>('');
  const [gerandoContas, setGerandoContas] = useState(false);

  useEffect(() => {
    if (!modalGerarContas) return;
    api.get<Array<{ id: number; nome: string }>>('/api/categorias-despesa')
      .then((r) => {
        setCategoriasDespesa(r);
        // default = "Fornecedores/Mercadoria" (importacao de NF e tipicamente compra
        // de mercadoria). Fallback exige AMBOS "fornecedor" + "mercadoria" pra nao
        // cair em "Credito de Fornecedor" e similares por engano.
        const padrao =
          r.find((c) => c.nome.trim().toLowerCase() === 'fornecedores/mercadoria')
          || r.find((c) => /fornecedor/i.test(c.nome) && /mercadoria/i.test(c.nome))
          || r[0];
        if (padrao) setCategoriaContas(String(padrao.id));
      })
      .catch(() => {});
  }, [modalGerarContas]);

  async function gerarContasPagar() {
    if (!modalGerarContas) return;
    if (!categoriaContas) { alert('Selecione a categoria de despesa'); return; }
    setGerandoContas(true);
    try {
      await api.post(`/api/contas-pagar/importar-nota/${modalGerarContas.notaId}`, {
        categoria_despesa_id: Number(categoriaContas),
        duplicatas: modalGerarContas.duplicatas,
      });
      const notaId = modalGerarContas.notaId;
      setModalGerarContas(null);
      setCategoriaContas('');
      setTimeout(() => navigate('/notas-entrada'), 200);
    } catch (e: any) {
      alert('Erro ao gerar contas: ' + e.message);
    } finally {
      setGerandoContas(false);
    }
  }

  // Configuracoes fiscais
  const [configFiscal, setConfigFiscal] = useState<ConfigFiscal | null>(null);

  function parseTotalNF(): number {
    const raw = totalNotaFiscal.replace(/\./g, '').replace(',', '.');
    return parseFloat(raw) || 0;
  }
  function parseCreditoFornecedor(): number {
    const raw = creditoFornecedor.replace(/\./g, '').replace(',', '.');
    return parseFloat(raw) || 0;
  }
  function parseOutrasDespesas(): number {
    const raw = outrasDespesas.replace(/\./g, '').replace(',', '.');
    return parseFloat(raw) || 0;
  }

  useEffect(() => {
    api.get<ConfigFiscal>('/api/configuracoes/fiscal').then(setConfigFiscal).catch(() => {});
  }, []);

  // ========================================================================
  // SANITY CHECK
  // ========================================================================
  function unidadeQtdEmContexto(sigla: string, prod: ProdutoVinc | null): number | null {
    if (!sigla) return null;
    const s = String(sigla).trim().toUpperCase();
    if (UNIDADES_FIXAS[s] !== undefined) return UNIDADES_FIXAS[s];
    if (prod && prod.unidade_compra && String(prod.unidade_compra).trim().toUpperCase() === s) {
      const q = parseFloat(prod.qtd_embalagem_padrao || '');
      if (Number.isFinite(q) && q > 0) return q;
    }
    return null;
  }

  function detectaAnomalia(item: ItemVinculado): { severidade: 'aviso' | 'critico'; mensagens: string[]; qt_emb_sugerido: number | null; detalhes: any[] } | null {
    if (!item.produto_vinculado) return null;
    const prod = item.produto_vinculado;
    const siglaNF = String(item.unidade || '').trim().toUpperCase();
    const siglaCad = String(prod.unidade_compra || prod.unidade_sigla || '').trim().toUpperCase();
    const qtdEmbAtual = item.qtd_embalagem ?? 1;
    const qtdNF = unidadeQtdEmContexto(siglaNF, prod);
    const qtdCad = unidadeQtdEmContexto(siglaCad, prod);
    // Nota na unidade do ESTOQUE de um produto comprado em pacote (cadastro PCT 10, vende UN, XML
    // em UN): a nota ja conta o estoque, e o 10 do cadastro e' o pacote da COMPRA, nao o Qt.Emb.
    // Sem isto o aviso sugeria Qt.Emb 0,1 e o custo "+700%" — e, desde 30/10/2026, toda Unidade de
    // Compra informada na importacao cai neste caso.
    const notaNaUnidadeDoEstoque = siglaNF !== '' && siglaNF === String(prod.unidade_sigla || '').trim().toUpperCase()
      && cadastroContaUnidadesDoEstoque({
        unidadeCompra: prod.unidade_compra, qtdEmbCadastro: prod.qtd_embalagem_padrao,
        unidadeVenda: prod.unidade_sigla, unidadeConteudo: prod.unidade_conteudo_embalagem,
      });
    const qtdEmbCad = notaNaUnidadeDoEstoque ? 1 : (parseFloat(prod.qtd_embalagem_padrao || '') || null);
    const anomalias: any[] = [];

    // Conversao compra -> venda pelo "Fator Conv." do cadastro. A NF veio na unidade de COMPRA e o
    // produto e vendido em outra (1 KG de cano de cobre = 3 MT): o fator E o Qt.Emb daquela linha.
    // Vem ANTES da heuristica de unidade divergente de proposito — o fator e um numero cadastrado
    // pra este produto, enquanto a outra sugestao deriva de tabela fixa (UN/DZ/CENTO/MILHEIRO) e da
    // Qtd na Emb.; com os dois presentes, quem manda e o especifico (o `.find` pega o primeiro).
    // Repare que a unidade da NF aqui BATE com o cadastro (KG = KG), entao o aviso de unidade
    // divergente nao cobre este caso: a divergencia que importa e compra vs VENDA.
    // Fica como AVISO e nao sobrescreve sozinho: o Qt.Emb pode ter vindo do vinculo do fornecedor,
    // ajustado a mao numa nota anterior. Quem aplica e o operador, no botao do banner.
    const fatorConv = parseFloat(prod.fator_conversao || '') || 0;
    const siglaCompra = String(prod.unidade_compra || '').trim().toUpperCase();
    const siglaVenda = String(prod.unidade_sigla || '').trim().toUpperCase();
    if (
      fatorConv > 0 && siglaCompra && siglaVenda && siglaCompra !== siglaVenda &&
      siglaNF === siglaCompra && Math.abs(qtdEmbAtual - fatorConv) > 0.0001
    ) {
      anomalias.push({
        tipo: 'fator_conversao',
        severidade: 'aviso',
        msg: `Cadastro converte ${siglaCompra} -> ${siglaVenda} pelo fator ${fatorConv}. Sugerido Qt.Emb=${fatorConv}`,
        qt_emb_sugerido: fatorConv,
      });
    }

    if (siglaNF && siglaCad && siglaNF !== siglaCad && !notaNaUnidadeDoEstoque) {
      let qtSug: number | null = null;
      if (qtdNF != null && qtdEmbCad && qtdEmbCad > 0) qtSug = Math.round((qtdNF / qtdEmbCad) * 10000) / 10000;
      else if (qtdNF != null && qtdCad != null && qtdCad > 0) qtSug = Math.round((qtdNF / qtdCad) * 10000) / 10000;
      anomalias.push({
        tipo: 'unidade_divergente',
        severidade: 'aviso',
        msg: `Unidade NF (${siglaNF}) ≠ cadastro (${siglaCad}).` + (qtSug != null ? ` Sugerido Qt.Emb=${qtSug}` : ''),
        qt_emb_sugerido: qtSug,
      });
    }

    const qtdEmb = parseFloat(String(qtdEmbAtual)) || 1;
    const custoNFporUnidadeCad = qtdEmbCad && qtdEmb > 0 ? (parseFloat(String(item.valor_unitario)) || 0) * (qtdEmbCad / qtdEmb) : null;
    const custoCadAtual = parseFloat(String(prod.preco_custo)) || 0;
    if (custoCadAtual > 0 && custoNFporUnidadeCad != null && custoNFporUnidadeCad > 0) {
      const variacao = ((custoNFporUnidadeCad - custoCadAtual) / custoCadAtual) * 100;
      if (Math.abs(variacao) > 30) {
        anomalias.push({
          tipo: 'variacao_custo',
          severidade: variacao < -30 ? 'critico' : 'aviso',
          msg: `Custo ${variacao > 0 ? '+' : ''}${variacao.toFixed(1)}% vs cadastrado (R$ ${custoCadAtual.toFixed(2)} → R$ ${custoNFporUnidadeCad.toFixed(2)}).`,
          variacao_pct: variacao,
        });
      }
    }
    if (anomalias.length === 0) return null;
    const severidade = anomalias.some(a => a.severidade === 'critico') ? 'critico' : 'aviso';
    return {
      severidade,
      mensagens: anomalias.map(a => a.msg),
      qt_emb_sugerido: anomalias.find(a => a.qt_emb_sugerido != null)?.qt_emb_sugerido ?? null,
      detalhes: anomalias,
    };
  }

  // ========================================================================
  // DIFAL
  // ========================================================================
  function calcDifal(item: ItemVinculado | ItemNF, emitenteUf: string | undefined, valorFrete: number, valorProdutos: number): number {
    const uf = String(emitenteUf || '').trim().toUpperCase();
    if (!uf || uf === 'GO' || !configFiscal) return 0;
    if (((item as any).valor_icms_st || 0) > 0) return 0;
    const usarBeneficio = configFiscal.fiscal_usar_beneficio === 'true';
    const aliqInterna = usarBeneficio
      ? parseFloat(configFiscal.fiscal_aliq_interna_efetiva) / 100
      : parseFloat(configFiscal.fiscal_aliq_interna_nominal) / 100;
    const ufsAliq7 = (configFiscal.fiscal_ufs_aliq_7 || '').split(',').map(s => s.trim());
    let aliqInter: number;
    const origemImportada = ['1', '2', '3', '8'].includes(String(item.origem));
    if (origemImportada) aliqInter = parseFloat(configFiscal.fiscal_aliq_inter_importados) / 100;
    else if (ufsAliq7.includes(uf)) aliqInter = parseFloat(configFiscal.fiscal_aliq_inter_sul_sudeste) / 100;
    else aliqInter = parseFloat(configFiscal.fiscal_aliq_inter_outros) / 100;
    if (aliqInterna <= aliqInter) return 0;
    const freteItem = valorProdutos > 0 ? (item.valor_total / valorProdutos) * (valorFrete || 0) : 0;
    const voper = item.valor_total + freteItem;
    return voper * (aliqInterna - aliqInter) / (1 - aliqInterna);
  }

  // Drag modal buscar produto
  const [modalPos, setModalPos] = useState({ x: 0, y: 0 });
  const dragRef = useRef<HTMLDivElement | null>(null);
  const handleDragStart = useCallback((e: React.MouseEvent) => {
    const el = dragRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const offX = e.clientX - rect.left;
    const offY = e.clientY - rect.top;
    function onMove(ev: MouseEvent) {
      setModalPos({ x: ev.clientX - offX, y: ev.clientY - offY });
    }
    function onUp() {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    }
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, []);

  async function processarParsed(data: NfeData) {
    setNfe(data);
    setTotalNotaFiscal(data.valor_total ? formatMoedaSemSimbolo(data.valor_total) : '');

    if (data.memorial) {
      if (data.memorial.outras_despesas > 0)
        setOutrasDespesas(formatMoedaSemSimbolo(data.memorial.outras_despesas));
      if (data.memorial.credito_fornecedor > 0)
        setCreditoFornecedor(formatMoedaSemSimbolo(data.memorial.credito_fornecedor));
    }

    let fornecedor: Fornecedor | null = null;
    const cnpj = data.emitente.cnpj;
    if (cnpj) {
      try {
        const fornecs = await api.get<Fornecedor[]>(`/api/notas-entrada/buscar-fornecedor/${cnpj}`);
        if (fornecs.length > 0) {
          fornecedor = fornecs[0]!;
          setFornecedorVinculado(fornecedor);
        }
      } catch {}
    }

    let mapeamento: Record<string, any> = {};
    if (fornecedor) {
      try {
        const mapData = await api.get<any[]>(`/api/notas-entrada/mapeamento-fornecedor/${fornecedor.id}`);
        for (const m of mapData) mapeamento[m.codigo_fornecedor] = m;
      } catch {}
    }

    const vinculados: ItemVinculado[] = await Promise.all(
      data.itens.map(async (item): Promise<ItemVinculado> => {
        const mapeado = mapeamento[item.codigo_fornecedor];
        if (mapeado) {
          const qSalvo = parseFloat(mapeado.qtd_embalagem);
          const fator = parseFloat(mapeado.fator_conversao) || 0;
          let qtdEmb: number;
          if (Number.isFinite(qSalvo) && qSalvo > 0) qtdEmb = Math.round(qSalvo * 10000) / 10000;
          else if (fator > 0) qtdEmb = Math.round(fator * 10000) / 10000;
          else qtdEmb = 1;
          return {
            ...item,
            produto_id: mapeado.produto_id,
            produto_vinculado: {
              id: mapeado.produto_id,
              codigo_interno: mapeado.codigo_interno,
              descricao: mapeado.descricao,
              marca_nome: mapeado.marca_nome ?? null,
              marca_id: mapeado.marca_id ?? null,
              ean: mapeado.ean,
              estoque_atual: mapeado.estoque_atual,
              foto_url: mapeado.foto_url,
              unidade_venda_id: mapeado.unidade_venda_id,
              unidade_sigla: mapeado.unidade_sigla,
              fator_conversao: mapeado.fator_conversao,
              unidade_compra: mapeado.unidade_compra,
              qtd_embalagem_padrao: mapeado.qtd_embalagem_padrao,
              unidade_conteudo_embalagem: mapeado.unidade_conteudo_embalagem,
              preco_custo: mapeado.preco_custo,
              uso_consumo: mapeado.uso_consumo,
            },
            emb_venda_id: mapeado.emb_venda_id ? parseInt(mapeado.emb_venda_id) : (mapeado.unidade_venda_id ? parseInt(mapeado.unidade_venda_id) : null),
            emb_venda_sigla: mapeado.emb_venda_sigla || mapeado.unidade_sigla || '',
            qtd_embalagem: qtdEmb > 0 ? qtdEmb : 1,
          };
        }
        let produto: ProdutoVinc | null = null;
        if (item.ean && item.ean !== 'SEM GTIN') {
          try {
            const prods = await api.get<ProdutoVinc[]>(`/api/notas-entrada/buscar-produto/${item.ean}`);
            if (prods.length > 0) produto = prods[0]!;
          } catch {}
        }
        if (produto) {
          const fator = parseFloat(produto.fator_conversao || '') || 0;
          return {
            ...item,
            produto_id: produto.id,
            produto_vinculado: produto,
            emb_venda_id: produto.unidade_venda_id ? produto.unidade_venda_id : null,
            emb_venda_sigla: produto.unidade_sigla || '',
            qtd_embalagem: fator > 0 ? Math.round(fator * 10000) / 10000 : 1,
          };
        }
        return { ...item, produto_id: null, produto_vinculado: null, emb_venda_id: null, emb_venda_sigla: '', qtd_embalagem: 1 };
      })
    );

    // NCM-ST batch lookup quando UF != GO
    const emitenteUf = data.emitente?.endereco?.uf || '';
    if (emitenteUf && emitenteUf !== 'GO') {
      try {
        const ncmsUnicos = [...new Set(vinculados.map(i => i.ncm).filter(Boolean))];
        if (ncmsUnicos.length > 0) {
          const ncmStList = await api.get<any[]>(`/api/ncm-st/buscar/${ncmsUnicos.join(',')}`);
          const ncmStMap: Record<string, any> = {};
          for (const n of ncmStList) ncmStMap[n.ncm] = n;
          for (let i = 0; i < vinculados.length; i++) {
            const item = vinculados[i]!;
            if ((item.valor_icms_st || 0) > 0) continue;
            const ncmSt = ncmStMap[item.ncm];
            if (!ncmSt) continue;
            const mva = parseFloat(ncmSt.mva_ajustado) || 0;
            const aliqInt = parseFloat(ncmSt.aliq_interna) / 100;
            const baseSt = item.valor_total * (1 + mva / 100);
            const icmsProprio = item.valor_total * (item.aliquota_icms / 100);
            const icmsSt = Math.max(0, baseSt * aliqInt - icmsProprio);
            vinculados[i] = { ...item, valor_icms_st: parseFloat(icmsSt.toFixed(2)), st_calculado: true };
          }
        }
      } catch {}
    }

    setItensVinculados(await aplicarSugestoesLocal(vinculados));

    // Auto-detect ST embutido
    const vST = vinculados.reduce((s, i) => s + (parseFloat(String(i.valor_icms_st)) || 0), 0);
    if (vST > 0) {
      const vNF = parseFloat(String(data.valor_total)) || 0;
      const vProd = parseFloat(String(data.valor_produtos)) || 0;
      const vFrete = parseFloat(String(data.valor_frete)) || 0;
      const vDesc = parseFloat(String(data.valor_desconto)) || 0;
      const totalSemST = vProd + vFrete - vDesc;
      const totalComST = totalSemST + vST;
      const tol = Math.max(1, vNF * 0.001);
      if (Math.abs(vNF - totalSemST) < tol) setStEmbutido(true);
      else if (Math.abs(vNF - totalComST) < tol) setStEmbutido(false);
      else setStEmbutido(false);
    } else setStEmbutido(false);
  }

  // Aviso imediato de nota duplicada — vale tanto pra XML por arquivo quanto
  // por consulta de chave na SEFAZ. Retorna true se a nota ja existe (cancela o fluxo).
  async function avisarSeJaImportada(data: NfeData): Promise<boolean> {
    if (!data.ja_importada) return false;
    const dt = new Date(data.ja_importada.criado_em).toLocaleString('pt-BR');
    await dialog.alerta(
      `Esta nota ja foi importada anteriormente.\n\n` +
      `NF ${data.numero}/${data.serie} · chave ${data.chave_acesso.slice(0, 8)}...\n` +
      `Importada em ${dt} (id #${data.ja_importada.id}).\n\n` +
      `Veja em /notas-entrada ou exclua a anterior se quiser reimportar.`,
      { titulo: 'Nota duplicada', tipo: 'aviso' },
    );
    return true;
  }

  async function processarArquivo(file: File | undefined | null) {
    if (!file || !file.name.toLowerCase().endsWith('.xml')) {
      setErro('Selecione um arquivo XML valido');
      return;
    }
    setErro('');
    setNfe(null);
    setLoading(true);
    try {
      const xml = await file.text();
      const data = await api.post<NfeData>('/api/notas-entrada/parse', { xml });
      // Pre-checagem de duplicata: backend ja diz se a chave_acesso esta no banco.
      // Mostra dialog.alerta imediato pra evitar o operador chegar ate "Confirmar Importacao".
      if (await avisarSeJaImportada(data)) return;
      await processarParsed(data);
    } catch (err: any) {
      setErro(err.message || 'Erro ao processar XML');
    } finally {
      setLoading(false);
    }
  }

  function baixarXmlBlob(chave: string, xml: string) {
    if (!xml) return;
    const blob = new Blob([xml], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `NFe${chave}.xml`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  async function consultarPorChave({ chave: chaveArg }: { chave?: string } = {}) {
    const chave = (chaveArg || chaveConsulta).replace(/\D/g, '');
    if (chave.length !== 44) {
      await dialog.alerta('A chave precisa ter 44 digitos numericos.');
      return;
    }
    if (!dvChaveValido(chave)) {
      await dialog.alerta('Chave com erro: digito verificador invalido. Confira a digitacao — provavelmente trocou 1 numero. Em DANFEs impressas, "3" e "2" sao facilmente confundidos.');
      return;
    }
    setErro('');
    setConsultando(true);
    setLoading(true);
    setNfe(null);
    try {
      const data = await api.post<NfeData & { _rawXml?: string }>('/api/notas-entrada/consultar-nfe', { chave });
      setModalConsultar(false);
      setChaveConsulta('');
      if (data?._rawXml) baixarXmlBlob(chave, data._rawXml);
      // Mesma pre-checagem de duplicata do fluxo por arquivo: avisa antes de montar a tela.
      if (await avisarSeJaImportada(data)) return;
      await processarParsed(data);
    } catch (err: any) {
      setErro(err.message || 'Falha ao consultar SEFAZ');
    } finally {
      setConsultando(false);
      setLoading(false);
    }
  }

  function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
    processarArquivo(e.target.files?.[0]);
  }
  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragging(false);
    processarArquivo(e.dataTransfer.files?.[0]);
  }

  // Porta unica do campo "Cod.": apagar o codigo SOLTA a linha (antes o campo vazio nao fazia nada e o
  // vinculo errado seguia de pe, invisivel).
  function aplicarCodigo(idx: number, termo: string) {
    const atual = itensVinculados[idx]?.produto_vinculado?.codigo_interno || '';
    if (!termo) {
      if (atual) desvincularProduto(idx);
      return;
    }
    // Redigitar o MESMO codigo RE-LE o cadastro — nao e no-op. Ate 2026-07-24 havia um
    // `if (termo !== atual)` aqui pra "nao refazer busca a toa", e ele engolia calado o unico gesto
    // que o operador tem pra dizer "atualiza essa linha": o produto ganhava o Fator Conv. no cadastro
    // com a importacao ja aberta, ele redigitava o codigo e a linha continuava com o dado velho.
    // A busca e um GET leve por codigo exato e reescreve a linha com o mesmo produto (idempotente).
    buscarProduto(idx, termo);
  }

  async function buscarProduto(idx: number, termo: string) {
    if (!termo) return;
    try {
      const prods = await api.get<ProdutoVinc[]>(`/api/notas-entrada/buscar-produto/${termo}`);
      if (prods.length > 0) {
        const novo = [...itensVinculados];
        const prod = prods[0]!;
        const fator = parseFloat(prod.fator_conversao || '') || 0;
        novo[idx] = {
          ...novo[idx]!,
          produto_id: prod.id,
          produto_vinculado: prod,
          emb_venda_id: prod.unidade_venda_id ?? novo[idx]!.emb_venda_id,
          emb_venda_sigla: prod.unidade_sigla || novo[idx]!.emb_venda_sigla,
          qtd_embalagem: fator > 0 ? Math.round(fator * 10000) / 10000 : novo[idx]!.qtd_embalagem,
          // Trocou de produto, a marca escolhida pro anterior nao vale mais: mantida, ela gravaria
          // no cadastro do produto NOVO uma marca que ninguem escolheu pra ele.
          _marca_produto: undefined,
        };
        setItensVinculados(novo);
        void recarregarLocalDaLinha(idx, prod.id);
      } else {
        // Nao achou: SOLTA a linha. Antes o vinculo errado ficava de pe e o operador via o produto
        // errado onde esperava o "+ Criar" — justamente o caso do produto que ainda nao existe.
        desvincularProduto(idx);
        setErro(`Produto nao encontrado para: ${termo} — linha desvinculada, use "+ Criar" pra cadastrar.`);
        setTimeout(() => setErro(''), 5000);
      }
    } catch {}
  }

  // Desvincular volta a linha ao estado de item nao vinculado da carga do XML (linha ~500): alem do
  // vinculo, larga a embalagem e a qtd. herdadas do produto ERRADO — senao a linha fica com dado dele
  // e o produto criado/vinculado depois nasce torto.
  function desvincularProduto(idx: number) {
    const novo = [...itensVinculados];
    novo[idx] = {
      ...novo[idx]!,
      produto_id: null,
      produto_vinculado: null,
      emb_venda_id: null,
      emb_venda_sigla: '',
      qtd_embalagem: 1,
      _qtd_emb_str: undefined,
      _marca_produto: undefined,
      localizacao_id: null,
      _lugares: [],
    };
    setItensVinculados(novo);
  }

  async function cadastrarFornecedor() {
    if (!nfe) return;
    setSalvandoFornecedor(true);
    try {
      const end = nfe.emitente.endereco;
      // O XML descreve uma INSCRICAO, nao a empresa: CNPJ, IE, endereco e UF nascem na FILIAL
      // (DA-311). A empresa fica so com o nome. Como o cadastro pelo XML tem uma inscricao so, ela e
      // a principal.
      // O nome fantasia nasce com a razao social (pedido do Ruan, 29/10/2026): vazio, o fornecedor
      // aparecia como "SEM NOME" / "Fornecedor 56" no Catalogo e ninguem sabia quem era — foi assim
      // que um CNPJ da BETEL virou empresa separada (DA-863). Da pra trocar depois em Fornecedores.
      const novoForn = await api.post<Fornecedor>('/api/fornecedores', {
        tipo_pessoa: 'PJ',
        nome_fantasia: nfe.emitente.nome,
        razao_social: nfe.emitente.nome,
        filiais: [{
          apelido: end.uf || 'MATRIZ',
          cpf_cnpj: nfe.emitente.cnpj,
          razao_social: nfe.emitente.nome,
          inscricao_estadual: nfe.emitente.ie,
          telefone: end.fone || null,
          cep: end.cep,
          endereco: end.logradouro,
          numero: end.numero,
          complemento: null,
          bairro: end.bairro,
          cidade: end.municipio,
          uf: end.uf,
          principal: true,
        }],
      });
      // Recasa pelo CNPJ pra ter empresa + filial na mao (o POST devolve a empresa; a nota precisa
      // saber de qual inscricao ela veio).
      let vinculado: Fornecedor = novoForn;
      if (nfe.emitente.cnpj) {
        try {
          const achados = await api.get<Fornecedor[]>(`/api/notas-entrada/buscar-fornecedor/${nfe.emitente.cnpj}`);
          if (achados.length > 0) vinculado = achados[0]!;
        } catch {}
      }
      setFornecedorVinculado(vinculado);
      setModalFornecedor(false);
    } catch (err: any) {
      setErro(err.message || 'Erro ao cadastrar fornecedor');
    } finally {
      setSalvandoFornecedor(false);
    }
  }

  async function buscarProdutos() {
    if (!buscaProd.trim()) return;
    setBuscandoProd(true);
    try {
      const data = await api.get<ProdutoVinc[]>('/api/produtos', { query: { q: buscaProd, com_inativos: '1' } });
      setResultadosProd(Array.isArray(data) ? data.slice(0, 20) : []);
    } catch {
      setResultadosProd([]);
    } finally {
      setBuscandoProd(false);
    }
  }

  // Liga a linha `idx` ao produto — o gesto da lupa e do combo "Da cotacao" da linha. Devolve false
  // quando recusou (produto inativo), pra quem chamou nao fechar o que estava aberto.
  function ligarProdutoNaLinha(idx: number, prod: ProdutoVinc): boolean {
    if (produtoInativo(prod)) { void dialog.alerta(MSG_PRODUTO_INATIVO); return false; }
    const novo = [...itensVinculados];
    const fator = parseFloat(prod.fator_conversao || '') || 0;
    novo[idx] = {
      ...novo[idx]!,
      produto_id: prod.id,
      produto_vinculado: prod,
      emb_venda_id: prod.unidade_venda_id ?? novo[idx]!.emb_venda_id,
      emb_venda_sigla: prod.unidade_sigla || novo[idx]!.emb_venda_sigla,
      qtd_embalagem: fator > 0 ? Math.round(fator * 10000) / 10000 : novo[idx]!.qtd_embalagem,
      _marca_produto: undefined,
    };
    setItensVinculados(novo);
    void recarregarLocalDaLinha(idx, prod.id);
    return true;
  }

  function selecionarProdutoBusca(prod: ProdutoVinc) {
    if (!modalBuscaProd) return;
    if (!ligarProdutoNaLinha(modalBuscaProd.idx, prod)) return;
    setModalBuscaProd(null);
    setBuscaProd('');
    setResultadosProd([]);
    setResultadosNovos([]);
  }

  /**
   * Casa esta linha do XML com um item da prateleira NOVOS PRODUTOS (DA-606).
   *
   * ⚠️ NAO vincula produto: o Novo Produto nao tem produto do cadastro — e essa a razao de ele
   * existir. A linha segue como "criar produto" (o XML e quem o cria, como sempre); o que a escolha
   * marca e QUEM aposentar no salvar. Sem produto E sem essa marca, a linha nao mudaria nada.
   */
  function ligarNovoProdutoNaLinha(idx: number, np: NovoProdutoBusca) {
    const novo = [...itensVinculados];
    const linha = novo[idx]!;
    novo[idx] = {
      ...linha,
      novo_produto_id: np.id,
      novo_produto_codigo: np.codigo,
      // A marca da prateleira so preenche o que esta VAZIO — o que o operador digitou manda.
      _marca_nova: linha._marca_nova || np.marca || undefined,
    };
    setItensVinculados(novo);
  }

  function selecionarNovoProdutoBusca(np: NovoProdutoBusca) {
    if (!modalBuscaProd) return;
    ligarNovoProdutoNaLinha(modalBuscaProd.idx, np);
    setModalBuscaProd(null);
    setBuscaProd('');
    setResultadosProd([]);
    setResultadosNovos([]);
  }

  /** O item da cotacao de origem que esta linha representa: pelo produto ligado ou pelo Novo Produto
   *  escolhido. Nunca por texto nem por codigo — a canonica do codigo mora so no banco (DA-471). */
  function itemCotacaoDaLinha(item: ItemVinculado): ItemCotacaoNota | null {
    if (itensCotacao.length === 0) return null;
    return itensCotacao.find(c =>
      (item.produto_id != null && c.produto?.id === item.produto_id)
      || (item.novo_produto_id != null && c.tipo === 'novo' && c.novo_produto?.id === item.novo_produto_id),
    ) ?? null;
  }

  /** Em qual linha da nota (#) um item da cotacao ja foi usado — a lupa avisa, sem impedir. */
  function linhaQueUsaItemCotacao(c: ItemCotacaoNota): number | null {
    const i = itensVinculados.findIndex(it =>
      (c.produto && it.produto_id === c.produto.id)
      || (c.novo_produto && it.novo_produto_id === c.novo_produto.id),
    );
    return i >= 0 ? i + 1 : null;
  }

  function escolherItemCotacao(c: ItemCotacaoNota) {
    if (c.produto) selecionarProdutoBusca(c.produto);
    else if (c.novo_produto) selecionarNovoProdutoBusca(c.novo_produto);
  }

  // O combo "Da cotacao" da LINHA (pedido dele, 31/10/2026: "uma forma de pesquisar, num combobox, o
  // item na Cotacao") — o mesmo efeito da secao da lupa, sem abrir a lupa. Limpar desfaz so o que a
  // cotacao ligou: o NP escolhido, ou o produto quando foi ele que casou com o item cotado.
  function escolherItemCotacaoNaLinha(idx: number, valor: string | number) {
    const item = itensVinculados[idx];
    if (!item) return;
    if (valor === '') {
      const atual = itemCotacaoDaLinha(item);
      if (!atual) return;
      if (atual.tipo === 'novo') desvincularNovoProduto(idx);
      else desvincularProduto(idx);
      return;
    }
    const c = itensCotacao.find(x => x.cotacao_item_id === Number(valor));
    if (!c) return;
    if (c.produto) ligarProdutoNaLinha(idx, c.produto);
    else if (c.novo_produto) ligarNovoProdutoNaLinha(idx, c.novo_produto);
  }

  const opcoesItemCotacao: OpcaoCombo[] = itensCotacao.map(c => {
    const escolhivel = !!(c.produto || c.novo_produto);
    return {
      value: c.cotacao_item_id,
      label: `${c.produto?.codigo_interno ?? c.novo_produto?.codigo ?? ''} ${c.descricao ?? ''}`.trim(),
      desabilitada: !escolhivel,
      nota: escolhivel ? undefined : (c.tipo === 'novo' ? 'ja comprado' : 'sem produto'),
      // Foto a esquerda na lista aberta (pedido dele, 31/10/2026).
      icone: (c.produto?.foto_url || c.novo_produto?.foto_url)
        ? <FotoProduto url={(c.produto?.foto_url || c.novo_produto?.foto_url)!} tam={32} />
        : <div style={{ width: 32, height: 32, borderRadius: 4, background: 'var(--grid-header-bg)' }} />,
    };
  });

  /** Desfaz o casamento com a prateleira, sem tocar no resto da linha. */
  function desvincularNovoProduto(idx: number) {
    const novo = [...itensVinculados];
    novo[idx] = { ...novo[idx]!, novo_produto_id: null, novo_produto_codigo: null };
    setItensVinculados(novo);
  }

  async function buscarFornecedores() {
    if (!buscaForn.trim()) return;
    setBuscandoForn(true);
    try {
      const data = await api.get<Fornecedor[]>('/api/fornecedores', { query: { busca: buscaForn.trim() } });
      setResultadosForn(Array.isArray(data) ? data : []);
    } catch {
      setResultadosForn([]);
    } finally {
      setBuscandoForn(false);
    }
  }

  async function vincularFornecedorExistente(forn: Fornecedor) {
    if (!nfe) return;
    setSalvandoFornecedor(true);
    try {
      const end = nfe.emitente.endereco;
      // Vincular a uma empresa que ja existe CRIA/COMPLETA a filial daquele CNPJ dentro dela — e o
      // caso "a Martins ja esta cadastrada e chegou a nota da filial de Minas" (DA-311).
      const atualizado = await api.patch<Fornecedor>(`/api/notas-entrada/vincular-fornecedor/${forn.id}`, {
        razao_social: nfe.emitente.nome,
        cpf_cnpj: nfe.emitente.cnpj,
        inscricao_estadual: nfe.emitente.ie,
        telefone: end.fone || null,
        endereco: end.logradouro,
        numero: end.numero,
        bairro: end.bairro,
        cidade: end.municipio,
        uf: end.uf,
        cep: end.cep,
        // cMun do emitente — sem ele nao da pra emitir devolucao de compra pra esta filial ([DA-559]).
        codigo_ibge_cidade: end.codigo_ibge_cidade || null,
      });
      setFornecedorVinculado(atualizado);
      setModalVincularForn(false);
      setBuscaForn('');
      setResultadosForn([]);
    } catch (err: any) {
      setErro(err.message || 'Erro ao vincular fornecedor');
    } finally {
      setSalvandoFornecedor(false);
    }
  }

  function resolverUnidadeVendaId(siglaNF: string): number | null {
    // So as ATIVAS (migration 0311): o produto novo nao nasce numa unidade que foi inativada.
    const ativas = unidades.filter(u => u.ativo !== false);
    if (ativas.length === 0) return null;
    const sigla = String(siglaNF || '').trim().toUpperCase();
    if (sigla) {
      const exato = ativas.find(u => String(u.sigla).toUpperCase() === sigla);
      if (exato) return exato.id;
    }
    const sinonimos: Record<string, string> = { PEC: 'UN', 'PÇ': 'PC', PC: 'UN' };
    if (sinonimos[sigla]) {
      const alt = ativas.find(u => String(u.sigla).toUpperCase() === sinonimos[sigla]);
      if (alt) return alt.id;
    }
    const un = ativas.find(u => String(u.sigla).toUpperCase() === 'UN');
    return un?.id ?? ativas[0]?.id ?? null;
  }

  /** pt-BR: "1.200,5" -> 1200.5. Nunca 0 nem negativo — 1 e o neutro ("a embalagem e a unidade"). */
  function parseQtdEmbNova(txt: string): number {
    const v = parseFloat(String(txt).replace(/\./g, '').replace(',', '.'));
    return Number.isFinite(v) && v > 0 ? Math.round(v * 10000) / 10000 : 1;
  }

  /**
   * Abre o modal de criar produto ja com a sugestao do XML: a unidade que a nota usa, se ela
   * existir no cadastro, e 1 na quantidade. A sugestao NAO adivinha a metragem pela descricao de
   * proposito — numero chutado que nasce preenchido e confirmado sem leitura, e ai vira dado.
   */
  function abrirModalCriarProduto(idx: number, item: ItemNF) {
    setNovoQtdEmb('1');
    setNovoUnVendaId(resolverUnidadeVendaId(item.unidade));
    setNovoCompraUn(null);
    setNovoCompraQtd(null);
    setModalProduto({ idx, item });
  }

  async function criarProduto() {
    if (!modalProduto) return;
    setSalvandoProduto(true);
    const item = modalProduto.item;
    // A flag Uso/Consumo vive na linha vinculada (ItemVinculado), nao no ItemNF do modal.
    const usoConsumoLinha = !!itensVinculados[modalProduto.idx]?.uso_consumo;
    try {
      const ean = (item.ean && item.ean !== 'SEM GTIN') ? item.ean : null;
      // Escolha do operador; a resolucao pela sigla do XML fica so como ULTIMO recurso (o combo
      // nasce com ela, entao so cai aqui se a lista de unidades ainda nao tinha carregado).
      const unidadeVendaId = novoUnVendaId ?? resolverUnidadeVendaId(item.unidade);
      const unVenda = unidades.find(u => u.id === unidadeVendaId) ?? null;
      const qtdEmb = parseQtdEmbNova(novoQtdEmb);
      const compraUn = (novoCompraUn ?? item.unidade ?? '').trim().toUpperCase() || null;
      const compraQtd = novoCompraQtd != null ? parseQtdEmbNova(novoCompraQtd) : qtdEmb;
      const novoProd = await api.post<ProdutoVinc>('/api/produtos', {
        descricao: item.descricao,
        ean,
        ncm: item.ncm || null,
        cest: item.cest || null,
        // Uso/Consumo nao e' revendido: o CFOP do XML fica como veio (regra dele, 30/10/2026).
        cfop: usoConsumoLinha ? (item.cfop || null) : cfopProdutoNovo(item.cfop),
        origem: item.origem || '0',
        cst_csosn: item.cst_icms || null,
        cst_pis: item.cst_pis || null,
        cst_cofins: item.cst_cofins || null,
        aliquota_icms: item.aliquota_icms || 0,
        aliquota_ipi: item.aliquota_ipi || 0,
        aliquota_pis: item.aliquota_pis || 0,
        aliquota_cofins: item.aliquota_cofins || 0,
        preco_custo: item.valor_unitario || 0,
        preco_pix: 0,
        estoque_atual: 0,
        unidade_venda_id: unidadeVendaId,
        // O trio do cadastro: a embalagem em que COMPRAMOS, QUANTOS cabem nela e em QUE unidade.
        // Ate 30/10/2026 era sempre a embalagem da nota com o Qt.Emb da linha; agora o modal
        // pergunta a compra a parte, porque o fornecedor pode faturar por UN e vender o PCT com 10
        // — e e' a compra que a cotacao conta ([DA-869]). Sem mexer, segue igual a nota. A unidade
        // do conteudo e' a de venda ([DA-600]: um combo so pros dois campos).
        unidade_compra: compraUn,
        qtd_embalagem_padrao: compraQtd,
        unidade_conteudo_embalagem: unVenda?.sigla || null,
        uso_consumo: usoConsumoLinha,
      });
      const novo = [...itensVinculados];
      novo[modalProduto.idx] = {
        ...novo[modalProduto.idx]!,
        produto_id: novoProd.id,
        produto_vinculado: novoProd,
        _marca_produto: undefined,
        // A linha herda o que acabou de ser cadastrado — e o mesmo gesto que os outros tres
        // caminhos de vinculo ja faziam lendo o cadastro ([mod_lancamentos] "O Qt.Emb puxa o
        // Fator Conv."). Esta era a UNICA porta que criava o cadastro e nao o lia de volta: a
        // linha ficava em Qt.Emb 1 e o catalogo nascia com a embalagem inteira como unitario.
        emb_venda_id: unidadeVendaId,
        emb_venda_sigla: unVenda?.sigla || '',
        qtd_embalagem: qtdEmb,
        _qtd_emb_str: String(qtdEmb).replace('.', ','),
        // Produto recem-criado nao tem endereco nenhum: a coluna fica vazia esperando a escolha —
        // e' a hora barata de endereca-lo, com a caixa na mao ([DA-448]).
        localizacao_id: null,
        _lugares: [],
      };
      setItensVinculados(novo);
      setModalProduto(null);
    } catch (err: any) {
      setErro(err.message || 'Erro ao criar produto');
    } finally {
      setSalvandoProduto(false);
    }
  }

  // Liga/desliga Uso/Consumo num produto JA vinculado (criado ou existente): grava
  // na propria ficha do produto (PUT) e reflete o selo na linha. Otimista — reverte
  // com toast se o backend recusar. Manter o selo visivel apos criar era o pedido:
  // antes o checkbox so existia na linha "Nao vinculado" e sumia ao virar produto.
  async function alternarUsoConsumoVinculado(idx: number) {
    const linha = itensVinculados[idx];
    const prod = linha?.produto_vinculado;
    if (!prod) return;
    const novoValor = !prod.uso_consumo;
    const aplicar = (v: boolean) => {
      setItensVinculados(atual => {
        const copia = [...atual];
        const alvo = copia[idx];
        if (!alvo?.produto_vinculado) return atual;
        copia[idx] = { ...alvo, produto_vinculado: { ...alvo.produto_vinculado, uso_consumo: v } };
        return copia;
      });
    };
    aplicar(novoValor);
    try {
      await api.put(`/api/produtos/${prod.id}`, { uso_consumo: novoValor });
      dialog.toast(novoValor ? 'Marcado como Uso/Consumo (escondido da venda).' : 'Voltou a ser vendavel.');
    } catch (err: any) {
      aplicar(!novoValor);
      dialog.toast(err.message || 'Nao foi possivel alterar Uso/Consumo.');
    }
  }

  async function criarTodosProdutos() {
    const naoVinculados = itensVinculados
      .map((item, idx) => ({ item, idx }))
      .filter(({ item }) => !item.produto_id);
    if (naoVinculados.length === 0) return;

    setSalvando(true);
    setErro('');
    const novo = [...itensVinculados];
    let criados = 0;
    let erroLocal = '';

    for (const { item, idx } of naoVinculados) {
      try {
        const ean = (item.ean && item.ean !== 'SEM GTIN') ? item.ean : null;
        const unidadeVendaId = resolverUnidadeVendaId(item.unidade);
        const novoProd = await api.post<ProdutoVinc>('/api/produtos', {
          descricao: item.descricao,
          ean,
          ncm: item.ncm || null,
          cest: item.cest || null,
          // Uso/Consumo nao e' revendido: o CFOP do XML fica como veio (regra dele, 30/10/2026).
          cfop: item.uso_consumo ? (item.cfop || null) : cfopProdutoNovo(item.cfop),
          origem: item.origem || '0',
          cst_csosn: item.cst_icms || null,
          cst_pis: item.cst_pis || null,
          cst_cofins: item.cst_cofins || null,
          aliquota_icms: item.aliquota_icms || 0,
          aliquota_ipi: item.aliquota_ipi || 0,
          aliquota_pis: item.aliquota_pis || 0,
          aliquota_cofins: item.aliquota_cofins || 0,
          preco_custo: item.valor_unitario || 0,
          preco_pix: 0,
          estoque_atual: 0,
          unidade_venda_id: unidadeVendaId,
          unidade_compra: item.unidade || null,
          uso_consumo: !!item.uso_consumo,
        });
        novo[idx] = { ...novo[idx]!, produto_id: novoProd.id, produto_vinculado: novoProd, localizacao_id: null, _lugares: [], _marca_produto: undefined };
        criados++;
      } catch (err: any) {
        erroLocal = `Erro ao criar "${item.descricao}": ${err.message}`;
        setErro(erroLocal);
        break;
      }
    }
    setItensVinculados(novo);
    if (criados > 0 && !erroLocal) {
      setSucesso(`${criados} produto${criados > 1 ? 's' : ''} criado${criados > 1 ? 's' : ''} com sucesso!`);
      setTimeout(() => setSucesso(''), 3000);
    }
    setSalvando(false);
  }

  // ---------------------------------------------------------------------------------------------
  // A previa do CATALOGO (DA-598). Pergunta ao backend, pra cada linha ja vinculada, se o codigo
  // desta nota alcanca uma linha do catalogo, se o produto ja tem outra linha com codigo diferente
  // (a duplicata que a ORCA produzia em silencio), ou se e' item inedito.
  //
  // Reroda pela ASSINATURA (produto + codigo de cada linha) e nao pela lista: `itensVinculados`
  // troca de referencia a cada tecla digitada em qualquer celula da grade.
  // ---------------------------------------------------------------------------------------------
  const assinaturaCat = itensVinculados.map(i => `${i.produto_id ?? ''}:${i.codigo_fornecedor ?? ''}`).join('|');
  const fornecedorCatId = fornecedorVinculado?.id ?? null;
  const filialCatId = fornecedorVinculado?.filial_id ?? null;
  const cnpjEmitente = nfe?.emitente?.cnpj ?? null;

  useEffect(() => {
    const req = ++reqCatRef.current;
    const alvos = itensVinculados
      .map((i, idx) => ({ idx, produto_id: i.produto_id, codigo_fornecedor: i.codigo_fornecedor }))
      .filter(a => !!a.produto_id);

    if (!fornecedorCatId || alvos.length === 0) {
      if (req === reqCatRef.current) { setDestinosCat([]); setErroPreviaCat(false); }
      return;
    }

    api.post<{ destinos: DestinoCatalogo[] }>('/api/notas-entrada/catalogo-previa', {
      fornecedor_id: fornecedorCatId,
      filial_id: filialCatId,
      fornecedor_cnpj: cnpjEmitente,
      itens: alvos.map(a => ({ produto_id: a.produto_id, codigo_fornecedor: a.codigo_fornecedor })),
    })
      .then(r => {
        // So vale a resposta da consulta MAIS NOVA: trocar de produto dispara outra, e a antiga
        // chegando depois repintaria o destino do produto anterior (a corrida do [DA-359]).
        if (req !== reqCatRef.current) return;
        setErroPreviaCat(false);
        const porIdx: Array<DestinoCatalogo | null> = itensVinculados.map(() => null);
        (r?.destinos ?? []).forEach((d, i) => {
          const alvo = alvos[i];
          if (alvo) porIdx[alvo.idx] = d;
        });
        setDestinosCat(porIdx);
        // Escolha feita pra uma linha que nao precisa mais decidir (trocou o produto, ou o codigo
        // passou a casar) nao pode sobreviver escondida e ir junto na gravacao.
        setEscolhaCat(prev => {
          const next: Record<number, number | 'novo'> = {};
          for (const [k, v] of Object.entries(prev)) {
            const d = porIdx[Number(k)];
            if (!d) continue;
            // A escolha so sobrevive se ainda existir a linha que ela aponta (o operador pode ter
            // trocado o produto da linha da nota, e ai o catalogo consultado e outro).
            if (v === 'novo' || d.linhas.some(c => c.id === v)) next[Number(k)] = v;
          }
          return next;
        });
      })
      .catch(() => {
        if (req !== reqCatRef.current) return;
        setDestinosCat([]);
        setErroPreviaCat(true);
      });
    // `assinaturaCat` e' a dep de verdade; a lista entra so pra leitura dentro do efeito.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assinaturaCat, fornecedorCatId, filialCatId, cnpjEmitente]);

  // Linhas que o operador ainda precisa resolver: o produto ja tem item no catalogo daquele
  // fornecedor, mas com OUTRO codigo — casar ou criar e' decisao de gente, e o Finalizar espera.
  const pendenciasCat = destinosCat
    .map((d, idx) => ({ d, idx }))
    .filter(({ d, idx }) => d?.situacao === 'decidir' && escolhaCat[idx] === undefined);

  /**
   * Quais linhas da nota estao pendentes, na numeracao que a coluna `#` da tabela mostra. Vale pros
   * TRES avisos (faixa do topo, motivo do botao e erro do Confirmar): contador sem o numero do item
   * obriga o operador a varrer a nota inteira procurando quem esta cobrando a decisao.
   */
  const rotulosPendenciasCat = pendenciasCat
    .map(p => `#${itensVinculados[p.idx]?.numero_item ?? p.idx + 1}`)
    .join(', ');

  /** Qual linha do catalogo esta marcada como destino desta compra (null = criar uma nova). */
  function linhaMarcadaId(idx: number): number | null {
    const escolha = escolhaCat[idx];
    if (escolha === 'novo') return null;
    if (typeof escolha === 'number') return escolha;
    return destinosCat[idx]?.item_casado_id ?? null;
  }

  /**
   * A marca de UMA linha do catalogo: o que ela tem hoje, com a correcao do operador por cima.
   * O campo EDITAVEL mora AQUI e so aqui — a coluna do item e' espelho so-leitura. Ter os dois
   * editaveis fazia um preencher o outro, e quem manda no cadastro e a linha ([DA-600] p.2).
   */
  function marcaDaLinhaCatalogo(c: CandidatoCatalogo): string {
    return marcaCat[c.id] ?? (c.marca ?? '');
  }

  /**
   * A marca que a COLUNA do item mostra E GRAVA: a do CADASTRO do produto (decisao dele,
   * 05/09/2026). Destino diferente do combo da linha do catalogo logo abaixo — um escreve em
   * `produtos.marca_id`, o outro em `catalogo_item.marca`. Os dois sao independentes.
   *
   * ⚠️ Isto NAO reabre o espelho que ele cortou na [DA-600] p.2 (*"Quando eu preencho uma Marca,
   * as duas sao preenchidas, nao pode"*). La os dois campos escreviam no MESMO registro, entao
   * preencher junto era bug. Aqui o catalogo so PROPOE um valor de partida pro cadastro, e so
   * quando o cadastro nao sabe a marca — o que ele grava continua sendo so dele.
   *
   * A origem vem junto porque na tela os tres casos sao indistinguiveis, e eles decidem coisas
   * diferentes: 'cadastro' e' o que o produto ja tem (nada e' proposto por cima); 'espelho' e' a
   * proposta vinda do catalogo em RASCUNHO — aqui o rascunho vale, porque a proposta tem que
   * acompanhar o que ele acabou de escolher no combo de baixo; 'operador' e' o que ele digitou.
   *
   * ⛔ `DIVERSOS` conta como marca preenchida (733 produtos usam) e nao e' sobreposta. Escolha
   * dele: espelha SO quando nao ha marca nenhuma no cadastro.
   */
  function marcaDoProdutoExibida(
    idx: number,
  ): { texto: string; origem: 'cadastro' | 'espelho' | 'operador' } | null {
    const item = itensVinculados[idx];
    if (!item) return null;
    if (item._marca_produto !== undefined) {
      const digitada = item._marca_produto.trim();
      return digitada ? { texto: digitada, origem: 'operador' } : null;
    }
    const doCadastro = (item.produto_vinculado?.marca_nome ?? '').trim();
    if (doCadastro) return { texto: doCadastro, origem: 'cadastro' };
    // Cadastro sem marca: propoe a da linha do catalogo que vai receber esta compra — a escolhida
    // no combo de baixo quando ha uma, o que o XML trouxe quando nao ha.
    const alvo = linhaMarcadaId(idx);
    const doCatalogo = alvo == null
      ? marcaDaLinhaNova(idx).trim()
      : (marcaCat[alvo] ?? destinosCat[idx]?.linhas.find(l => l.id === alvo)?.marca ?? '').trim();
    if (doCatalogo) return { texto: doCatalogo, origem: 'espelho' };
    const doXml = (item.marca_nf ?? '').trim();
    if (doXml) return { texto: doXml, origem: 'espelho' };
    return null;
  }

  /** A marca da linha que a importacao vai CRIAR (a opcao "criar uma linha nova"). */
  function marcaDaLinhaNova(idx: number): string {
    const item = itensVinculados[idx];
    return item?._marca_nova ?? item?.marca_nf ?? '';
  }

  /**
   * Embalagem e quantidade da linha que a importacao vai CRIAR. Sem edicao elas mostram o que o
   * backend gravaria — a MESMA peca do shared (`embalagemDaLinhaNovaPelaNota`): a embalagem do
   * cadastro quando o XML veio na unidade do conteudo, senao a da nota. Assim a celula e' a previa
   * do registro, e nao um branco.
   */
  function sugestaoLinhaNova(idx: number) {
    const item = itensVinculados[idx];
    return embalagemDaLinhaNovaPelaNota({
      embalagemXml: item?.unidade,
      qtdEmbItem: item?.qtd_embalagem,
      unidadeCompra: item?.produto_vinculado?.unidade_compra,
      qtdEmbCadastro: item?.produto_vinculado?.qtd_embalagem_padrao,
      unidadeVenda: item?.produto_vinculado?.unidade_sigla,
      unidadeConteudo: item?.produto_vinculado?.unidade_conteudo_embalagem,
    });
  }
  function embDaLinhaNova(idx: number): string {
    return itensVinculados[idx]?._cat_emb_nova ?? sugestaoLinhaNova(idx).embalagem;
  }
  function qtdDaLinhaNova(idx: number): string {
    return itensVinculados[idx]?._cat_qtd_nova ?? String(sugestaoLinhaNova(idx).quantidade).replace('.', ',');
  }

  /**
   * O que a linha EXISTENTE marcada guarda se ninguem mexer: a embalagem e a quantidade dela. Sem
   * quantidade, o backend a trata como linha nova e grava a sugestao — a previa mostra o mesmo.
   */
  function pernaSemEdicao(idx: number, c: CandidatoCatalogo): { emb: string; qtd: number; adotada?: 'cadastro' | 'irma' } {
    // Linha em UNIDADE adota a embalagem maior que o cadastro ou o outro CNPJ conhecem — a mesma
    // peca que o servidor chama ao gravar (regra dele, 30/10/2026).
    const item = itensVinculados[idx];
    const pv = item?.produto_vinculado;
    const adotada = embalagemAdotadaPelaLinha({
      embalagemXml: item?.unidade, qtdEmbItem: item?.qtd_embalagem,
      unidadeCompra: pv?.unidade_compra, qtdEmbCadastro: pv?.qtd_embalagem_padrao,
      unidadeVenda: pv?.unidade_sigla, unidadeConteudo: pv?.unidade_conteudo_embalagem,
      embalagemLinha: c.embalagem, qtdLinha: c.quantidade_embalagem,
      irma: c.irma_embalagem ? { embalagem: c.irma_embalagem, quantidade: c.irma_quantidade } : null,
    });
    if (adotada) return { emb: adotada.embalagem, qtd: adotada.quantidade, adotada: adotada.origem };
    // Sem embalagem tambem: o backend a trata como linha nova (a quantidade sozinha nao diz o que e').
    const q = Number(c.quantidade_embalagem ?? 0);
    if (q > 0 && String(c.embalagem || '').trim() !== '') return { emb: String(c.embalagem).toUpperCase(), qtd: q };
    const s = sugestaoLinhaNova(idx);
    return { emb: s.embalagem, qtd: s.quantidade };
  }

  /** Edicao da linha EXISTENTE marcada, se ainda vale (a marcacao nao mudou desde que ele digitou). */
  function edicaoPerna(idx: number): { emb?: string; qtd?: string } | null {
    const e = pernaCat[idx];
    return e && e.linha === linhaMarcadaId(idx) ? e : null;
  }
  function editarPerna(idx: number, linha: number, campo: 'emb' | 'qtd', valor: string) {
    setPernaCat(prev => {
      const atual = prev[idx]?.linha === linha ? prev[idx]! : { linha };
      return { ...prev, [idx]: { ...atual, [campo]: valor } };
    });
  }
  /**
   * O preco da embalagem que a linha do catalogo vai gravar, pelas pecas do servidor. A Qtd da
   * linha e' o CONTEUDO (100 parafusos): quem diz quanto do estoque 1 embalagem vale e'
   * `estoqueNaEmbalagemDaLinha` — a CX de 100 parafusos e' 1 caixa no estoque.
   */
  function precoEmbDaLinha(
    idx: number, embLinha: string | null | undefined, qtdLinha: number | string | null | undefined,
    precoAtual?: number | null,
  ): number | null {
    const item = itensVinculados[idx];
    const pv = item?.produto_vinculado;
    const estoque = estoqueNaEmbalagemDaLinha({ embalagem: embLinha, quantidade: qtdLinha, precoAtual }, {
      unidadeCompra: pv?.unidade_compra, qtdEmbCadastro: pv?.qtd_embalagem_padrao,
      unidadeVenda: pv?.unidade_sigla, unidadeConteudo: pv?.unidade_conteudo_embalagem,
    }, {
      embalagemXml: String(item?.unidade || '').trim().toUpperCase() || 'UN',
      qtdEmbItem: item?.qtd_embalagem,
      precoXml: item?.valor_unitario,
    });
    return precoDaEmbalagemPelaNota(item?.valor_unitario, item?.qtd_embalagem, estoque);
  }
  function editarCatalogoNovo(idx: number, campo: '_cat_emb_nova' | '_cat_qtd_nova', valor: string) {
    const novo = [...itensVinculados];
    novo[idx] = { ...novo[idx]!, [campo]: valor };
    setItensVinculados(novo);
  }

  /**
   * Copia a quantidade da linha do catalogo pro Qt.Emb do ITEM — que e' quem decide estoque e
   * custo. E' a porta de reparo do aviso de divergencia: sem ela o operador leria o problema e
   * teria de achar sozinho a celula certa, tres colunas adiante, com o mesmo nome.
   */
  function aplicarQtdDoCatalogoNoItem(idx: number) {
    const q = parseQtdEmbNova(qtdDaLinhaNova(idx));
    const novo = [...itensVinculados];
    novo[idx] = { ...novo[idx]!, qtd_embalagem: q, _qtd_emb_str: formatQtdBr(q) };
    setItensVinculados(novo);
  }

  /** 1000 -> "1000"; 0,5 -> "0,5". Sem separador de milhar: e' valor de campo, nao de leitura. */
  function formatQtdBr(n: number): string {
    return String(n).replace('.', ',');
  }

  // As embalagens que o combo oferece: as unidades cadastradas MAIS a sigla que a nota usa quando
  // ela nao existe na tabela (a ORCA fatura em RLO, que nao e' unidade cadastrada). Sem a uniao, a
  // celula abriria VAZIA justamente nas notas em que a embalagem e' a informacao que falta —
  // mesmo motivo do combo de marca ([DA-600] p.5).
  const opcoesEmbalagemCat: string[] = (() => {
    // Inativas (migration 0311) ficam de fora, salvo a que alguma linha ja escolheu.
    const siglas = new Set<string>(unidades.filter(u => u.ativo !== false).map(u => String(u.sigla).toUpperCase()));
    itensVinculados.forEach(i => { const s = String(i.unidade || '').trim().toUpperCase(); if (s) siglas.add(s); });
    itensVinculados.forEach((_, idx) => { const s = String(embDaLinhaNova(idx) || '').trim().toUpperCase(); if (s) siglas.add(s); });
    return [...siglas].sort((a, b) => a.localeCompare(b));
  })();

  function editarMarca(idx: number, valor: string, idLinha: number | null) {
    const v = valor.toUpperCase().slice(0, 100);
    if (idLinha == null) {
      const novo = [...itensVinculados];
      novo[idx] = { ...novo[idx]!, _marca_nova: v };
      setItensVinculados(novo);
      return;
    }
    setMarcaCat(prev => ({ ...prev, [idLinha]: v }));
  }

  /**
   * A marca da coluna do ITEM — a do cadastro do produto. Grava sempre em `_marca_produto`,
   * inclusive vazio: a partir do primeiro toque a celula para de espelhar e passa a valer o que
   * ele escreveu. Limpar o campo e' um gesto legitimo ("nao quero por marca nenhuma"), e nao pode
   * ser lido como "volta a espelhar" — senao apagar traz de volta o valor que ele acabou de tirar.
   */
  function editarMarcaProduto(idx: number, valor: string) {
    const novo = [...itensVinculados];
    novo[idx] = { ...novo[idx]!, _marca_produto: valor.toUpperCase().slice(0, 100) };
    setItensVinculados(novo);
  }

  // As opcoes do combo: as marcas cadastradas MAIS as que as linhas do catalogo ja usam. A uniao
  // nao e' capricho — 9 das 420 linhas com marca usam nome que nao esta na tabela `marcas`, e um
  // combo que so lista a tabela mostraria essas linhas VAZIAS, que se le como "sem marca".
  const opcoesMarca: OpcaoCombo[] = (() => {
    const nomes = new Set<string>(marcas.map(m => m.nome));
    destinosCat.forEach(d => (d?.linhas ?? []).forEach(l => { if (l.marca) nomes.add(l.marca); }));
    Object.values(marcaCat).forEach(m => { if (m) nomes.add(m); });
    itensVinculados.forEach(i => { if (i.marca_nf) nomes.add(i.marca_nf); });
    // Pelo mesmo motivo, a marca que o CADASTRO do produto ja tem: sem ela o combo da coluna do
    // item abriria vazio num produto que TEM marca, e confirmar apagaria o dado sem ninguem pedir.
    itensVinculados.forEach(i => {
      const m = (i.produto_vinculado?.marca_nome ?? '').trim();
      if (m) nomes.add(m);
      if (i._marca_produto) nomes.add(i._marca_produto);
    });
    return [...nomes].sort((a, b) => a.localeCompare(b)).map(n => ({ value: n, label: n }));
  })();

  // Duplicata que JA existe: o codigo desta nota casa com uma linha, mas o produto tem outras
  // naquele mesmo fornecedor. Nao trava a importacao (o estrago ja esta feito, e nao e aqui que se
  // conserta) — mas nao pode ficar calada, senao a tela diz "ja cadastrado" e a duplicata segue
  // invisivel, que foi como as 5 da ORCA passaram 4 dias sem ninguem ver.
  const duplicadasCat = destinosCat.filter(d => d && d.linhas.length > 1).length;

  // O que impede de confirmar, dito ANTES do clique. Ate aqui os portoes so falavam depois de
  // clicar, como erro vermelho no topo — e o operador descia a nota inteira sem saber que ela nao
  // ia passar. Botao apagado sem motivo escrito seria pior ainda ("Botao desabilitado DIZ por que",
  // convencao da casa), entao a razao anda colada nele e leva ate a linha culpada.
  // ⚠️ A conferencia do catalogo FALHA ABERTA (DA-598): se ela nao respondeu, o portao se cala —
  // nota nao deixa de entrar por causa de checagem de cadastro.
  /**
   * ⛔ Item com produto vinculado NAO entra sem prateleira escolhida ([DA-618]).
   *
   * A regra mora no `shared` (`prateleira-obrigatoria.ts`) porque vale nos DOIS lados: aqui ela
   * trava o botao e diz o que falta, no backend ela recusa a gravacao. As isencoes e o porque de
   * cada uma estao la — nao duplicar a explicacao aqui, senao as duas copias divergem.
   *
   * Custa quase nada no dia a dia porque a coluna ja NASCE preenchida: o `localizacoes-sugeridas`
   * devolve o fundo da fila e a tela adota ([DA-448]). Medido em prod: desde 05/08 so 20 itens
   * escaparam. O caso que a trava fecha e' o caro — produto SEM endereco nenhum, que entrava e
   * ficava com estoque em lugar nenhum (141 produtos da loja estao assim).
   */
  const semLugar = itensSemPrateleira(itensVinculados, atualizarEstoque);
  const rotulosSemLugar = rotulosSemPrateleira(itensVinculados, semLugar);

  const bloqueio: { msg: string; alvo?: string } | null = (() => {
    if (!nfe) return null;
    if (!fornecedorVinculado) return { msg: 'vincule o fornecedor da nota' };
    const semProduto = itensVinculados.filter(i => !i.produto_id).length;
    if (semProduto > 0) return { msg: `${contar(semProduto, 'item')} sem produto vinculado` };
    if (semLugar.length > 0) {
      return {
        msg: `falta dizer a prateleira de ${contar(semLugar.length, 'item')} (${rotulosSemLugar})`,
        alvo: '[data-sem-lugar="1"]',
      };
    }
    if (erroPreviaCat) return null;
    if (itensVinculados.some((i, idx) => i.produto_id && !destinosCat[idx])) {
      return { msg: 'conferindo o catalogo deste fornecedor...' };
    }
    if (pendenciasCat.length > 0) {
      return {
        msg: `falta dizer o destino no catalogo de ${contar(pendenciasCat.length, 'item')} (${rotulosPendenciasCat})`,
        alvo: '[data-pendencia-catalogo="1"]',
      };
    }
    // Regra dele, 30/10/2026: a importacao PEDE a Unidade de Compra de quem nao tem — uma vez por
    // produto; respondida, fica na ficha.
    const semCompra = [...new Set(itensVinculados.filter(i => faltaCompra(i.produto_vinculado)).map(i => i.produto_id))].length;
    if (semCompra > 0) {
      return {
        msg: `falta a Unidade de Compra de ${contar(semCompra, 'produto')}`,
        alvo: '[data-sem-compra="1"]',
      };
    }
    return null;
  })();

  /** O cadastro nao diz como o produto e comprado (sem Unidade de Compra ou sem Qtd na Emb). */
  function faltaCompra(pv: ProdutoVinc | null | undefined): boolean {
    if (!pv) return false;
    return !String(pv.unidade_compra || '').trim() || !(Number(pv.qtd_embalagem_padrao) > 0);
  }

  /** Leva a tela ate a primeira linha culpada. Sem alvo na tela, sobe pro topo. */
  function irParaPendencia(seletor: string) {
    const alvo = document.querySelector<HTMLElement>(seletor);
    if (alvo) alvo.scrollIntoView({ behavior: 'smooth', block: 'center' });
    else window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function confirmarImportacao() {
    if (!nfe) return;
    setErro('');

    if (!fornecedorVinculado) {
      setErro('Vincule o fornecedor antes de importar. Use "Vincular Existente" ou "Cadastrar Novo" na secao do fornecedor.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    const naoVinculados = itensVinculados.filter(i => !i.produto_id);
    if (naoVinculados.length > 0) {
      setErro(`${naoVinculados.length} item(ns) sem produto vinculado. Vincule ou crie os produtos antes de importar.`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    // A trava da prateleira ([DA-618]). Repetida aqui alem do `bloqueio` porque o botao desabilitado
    // e' so a primeira camada: quem chega por Enter, por aba velha ou por corrida de estado passaria.
    if (semLugar.length > 0) {
      setErro(
        `${contar(semLugar.length, 'item')} sem prateleira (${rotulosSemLugar}). ` +
        'Escolha na coluna LOCAL onde a mercadoria vai ser guardada — e' +
        ' de la que o estoque sai se esta nota for desfeita. Produto que ainda nao tem endereco' +
        ' mostra o cadastro inteiro de prateleiras nessa coluna.',
      );
      irParaPendencia('[data-sem-lugar="1"]');
      return;
    }
    // O portao do catalogo (DA-598) cobra uma DECISAO, nunca uma confirmacao de leitura: so trava
    // onde o produto ja tem linha naquele fornecedor com outro codigo, que e o unico caso em que o
    // sistema nao consegue escolher sozinho. Marcar "li e confiro" viraria reflexo numa semana; ter
    // que dizer "e este item" ou "criar novo" nao vira, porque a resposta muda o resultado.
    if (pendenciasCat.length > 0) {
      setErro(
        `${contar(pendenciasCat.length, 'item')} ${pendenciasCat.length === 1 ? 'precisa' : 'precisam'} de uma escolha no catalogo (${rotulosPendenciasCat}): o produto ja tem cadastro neste fornecedor com outro codigo. Na linha "catalogo" de cada um, marque de qual oferta e esta compra, ou peca uma linha nova.`,
      );
      const alvo = document.querySelector<HTMLElement>('[data-pendencia-catalogo="1"]');
      if (alvo) alvo.scrollIntoView({ behavior: 'smooth', block: 'center' });
      else window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setSalvando(true);
    try {
      const totalIPI = itensVinculados.reduce((s, it) => s + (parseFloat(String(it.valor_ipi)) || 0), 0);
      const totalNF = (parseTotalNF() || nfe.valor_total) + parseOutrasDespesas();
      const somaXml = nfe.valor_produtos || 0;
      const custosCompartilhados = totalNF - somaXml - totalIPI;
      const fatorShared = (somaXml > 0 && custosCompartilhados > 0) ? custosCompartilhados / somaXml : 0;

      const itens = itensVinculados.map((i, idx) => {
        const qtdReal = i.quantidade * (i.qtd_embalagem ?? 1);
        const custoCompraUnit = qtdReal > 0 ? (i.valor_total * (1 + fatorShared) + (parseFloat(String(i.valor_ipi)) || 0)) / qtdReal : i.valor_unitario;
        const difal = calcDifal(i, nfe?.emitente?.endereco?.uf, nfe?.valor_frete || 0, nfe?.valor_produtos || 0);
        const stTotal = stEmbutido ? 0 : (i.valor_icms_st || 0);
        const impostoUnit = qtdReal > 0 ? (difal > 0 ? difal : stTotal) / qtdReal : 0;
        const custoReal = custoCompraUnit + impostoUnit;
        return {
          numero_item: i.numero_item,
          produto_id: i.produto_id,
          codigo_fornecedor: i.codigo_fornecedor,
          ean: i.ean,
          descricao: i.descricao,
          // A marca do ITEM e a do XML — a menos que o operador tenha escolhido uma na linha
          // "criar uma linha nova", que e a marca com que essa linha nasce. ⚠️ So vale se a
          // escolha AINDA e criar linha nova: escolher a marca e depois marcar uma linha
          // existente mandaria essa marca pisar numa linha que nao era o alvo.
          marca_nf: (linhaMarcadaId(idx) == null ? i._marca_nova : undefined) ?? i.marca_nf,
          // Escolha de gente vence a marca que a linha ja tinha; XML cru nao (DA-307).
          marca_manual: linhaMarcadaId(idx) == null && i._marca_nova != null,
          // A Marca do CADASTRO do produto (a coluna do ITEM). Destino diferente do `marca_nf`
          // acima, que e' do catalogo do fornecedor. So viaja quando MUDA o que o produto tem
          // hoje: mandar o valor igual seria um UPDATE que nao muda nada, e — o que importa —
          // isso e' o que garante que a SUGESTAO nunca sobrescreve cadastro que ja sabe a marca.
          // A regra e' dele (05/09/2026): espelhar so quando nao ha marca no cadastro.
          marca_produto: (() => {
            if (!i.produto_id) return undefined;
            const escolhida = (marcaDoProdutoExibida(idx)?.texto ?? '').trim();
            if (!escolhida) return undefined;
            const atual = (i.produto_vinculado?.marca_nome ?? '').trim();
            return escolhida.toUpperCase() === atual.toUpperCase() ? undefined : escolhida;
          })(),
          // Embalagem/quantidade digitadas na linha "criar uma linha nova". So viajam quando a
          // escolha AINDA e' criar linha nova — digitar aqui e depois marcar uma linha existente
          // mandaria esses valores pisarem numa oferta que nao era o alvo ([DA-600] p.6). Ausentes
          // quando ninguem tocou: o backend segue derivando da nota, como sempre fez.
          catalogo_embalagem: linhaMarcadaId(idx) == null ? i._cat_emb_nova : undefined,
          catalogo_qtd_embalagem: linhaMarcadaId(idx) == null && i._cat_qtd_nova != null
            ? parseQtdEmbNova(i._cat_qtd_nova)
            : undefined,
          // A mesma correcao na linha EXISTENTE marcada. So o que ele mexeu; ausente, a linha
          // guarda a embalagem que ja tem e o backend so converte o preco pra ela.
          catalogo_perna_embalagem: edicaoPerna(idx)?.emb || undefined,
          catalogo_perna_qtd: edicaoPerna(idx)?.qtd != null ? parseQtdEmbNova(edicaoPerna(idx)!.qtd!) : undefined,
          ncm: i.ncm,
          cest: i.cest,
          cfop: i.cfop,
          origem: i.origem,
          unidade: i.unidade,
          quantidade: i.quantidade,
          qtd_real: qtdReal,
          valor_unitario: i.valor_unitario,
          valor_total: i.valor_total,
          cst_icms: i.cst_icms,
          cst_pis: i.cst_pis,
          cst_cofins: i.cst_cofins,
          aliquota_icms: i.aliquota_icms,
          aliquota_ipi: i.aliquota_ipi,
          aliquota_pis: i.aliquota_pis,
          aliquota_cofins: i.aliquota_cofins,
          valor_icms: i.valor_icms,
          valor_ipi: i.valor_ipi,
          valor_pis: i.valor_pis,
          valor_cofins: i.valor_cofins,
          valor_icms_st: stTotal,
          valor_difal: difal,
          custo_real: custoReal,
          info_adicional: i.info_adicional,
          emb_venda_id: i.emb_venda_id ?? null,
          // Onde guardar ([DA-448]). Nulo = o backend decide como sempre decidiu (cascata).
          localizacao_id: i.localizacao_id ?? null,
          // "É este item do catalogo" ([DA-598]): o backend adota o codigo desta nota como
          // ALTERNATIVO daquela linha, entao ela e atualizada em vez de nascer uma segunda.
          // 'novo' e ausencia dao no mesmo caminho — o de sempre —, mas so 'novo' foi ESCOLHIDO.
          catalogo_item_id: typeof escolhaCat[idx] === 'number' ? escolhaCat[idx] : null,
          // "É este item da prateleira" (DA-606): ao salvar, o Novo Produto vira `comprado` e a
          // identidade dele migra pro Catalogo do fornecedor DESTA nota. Escolha do operador.
          novo_produto_id: i.novo_produto_id ?? null,
        };
      });

      const body = {
        chave_acesso: nfe.chave_acesso,
        numero: nfe.numero,
        serie: nfe.serie,
        data_emissao: nfe.data_emissao,
        natureza_operacao: nfe.natureza_operacao,
        fornecedor_id: fornecedorVinculado?.id || null,
        cotacao_id: cotacaoOrigemId,
        filial_id: fornecedorVinculado?.filial_id || null,
        fornecedor_cnpj: nfe.emitente.cnpj,
        fornecedor_nome: nfe.emitente.nome,
        valor_produtos: nfe.valor_produtos,
        valor_frete: nfe.valor_frete,
        valor_desconto: nfe.valor_desconto,
        valor_total: nfe.valor_total,
        transportadora: nfe.transportadora,
        peso_bruto: nfe.peso_bruto,
        volumes: nfe.volumes,
        informacoes_complementares: nfe.informacoes_complementares,
        atualizar_estoque: atualizarEstoque,
        outras_despesas: parseOutrasDespesas(),
        credito_fornecedor: parseCreditoFornecedor(),
        total_nota_fiscal: parseTotalNF() || nfe.valor_total,
        itens,
        // Marcas corrigidas na linha do catalogo. So o que MUDOU de verdade — reenviar o valor
        // igual carimbaria `atualizado_em` de linha que ninguem tocou, e ai o catalogo passa a
        // mentir sobre quando foi mexido. Vazio nao entra: campo limpo nao apaga marca — pra apagar
        // de verdade existe o X no modal do Catalogo.
        catalogo_marcas: Object.entries(marcaCat)
          .map(([id, marca]) => ({ item_id: Number(id), marca: (marca || '').trim() }))
          .filter(m => {
            if (!m.marca) return false;
            const original = destinosCat
              .flatMap(d => d?.linhas ?? [])
              .find(l => l.id === m.item_id)?.marca ?? '';
            return m.marca !== original;
          }),
      };

      const notaSalva = await api.post<{ id: number }>('/api/notas-entrada', body);
      setSucesso('Nota importada com sucesso!');

      if (nfe.duplicatas && nfe.duplicatas.length > 0) {
        setModalGerarContas({
          notaId: notaSalva.id,
          duplicatas: nfe.duplicatas,
          valorTotalNF: parseTotalNF() || nfe.valor_total,
          creditoFornecedor: parseCreditoFornecedor(),
          outrasDespesas: parseOutrasDespesas(),
        });
      } else {
        setTimeout(() => navigate(`/notas-entrada/${notaSalva.id}/revisar-margens`), 1500);
      }
    } catch (err: any) {
      const msg = err?.message || 'Erro ao importar nota';
      // Mensagens de regra de negocio sobem como dialog.alerta global (mais visivel
      // que o banner vermelho do topo, que costuma passar batido).
      if (/ja foi importada/i.test(msg)) {
        await dialog.alerta(msg, { titulo: 'Nota duplicada', tipo: 'aviso' });
      } else {
        await dialog.alerta(msg, { titulo: 'Erro ao importar', tipo: 'erro' });
      }
    } finally {
      setSalvando(false);
    }
  }

  const totalVinculados = itensVinculados.filter(i => i.produto_id).length;
  const totalNaoVinculados = itensVinculados.length - totalVinculados;

  return (
    <div>
      <div className="pagina-header">
        <h2 className="pagina-titulo" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <button
            type="button"
            onClick={() => navigate('/notas-entrada')}
            className="btn-icon"
            title="Voltar"
            aria-label="Voltar"
            style={{ marginRight: 4 }}
          >
            <ArrowLeft size={20} />
          </button>
          Importar XML de NF-e
        </h2>
      </div>

      {erro && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
          <div className="flex items-start justify-between gap-4">
            <span className="flex-1">{erro}</span>
            <button onClick={() => setErro('')} className="text-red-400 hover:text-red-600"><X size={16} /></button>
          </div>
        </div>
      )}
      {sucesso && <div className="mb-4 p-3 bg-green-50 text-green-700 rounded-lg text-sm">{sucesso}</div>}

      {!nfe && (
        <div
          className={`border-2 border-dashed rounded-xl p-12 text-center transition ${dragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}`}
          style={{ backgroundColor: dragging ? undefined : 'var(--card-bg)' }}
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
        >
          {loading ? (
            <div className="muted">
              <Loader2 size={32} className="mx-auto mb-2 animate-spin" />
              Processando XML...
            </div>
          ) : (
            <>
              <Upload size={48} className="mx-auto mb-4 opacity-40" />
              <p className="mb-2">Arraste o arquivo XML da NF-e aqui</p>
              <p className="text-sm muted mb-4">ou</p>
              <div className="flex items-center justify-center gap-3 flex-wrap">
                <label className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition cursor-pointer text-sm">
                  <FileText size={16} /> Selecionar arquivo
                  <input type="file" accept=".xml" onChange={handleUpload} className="hidden" />
                </label>
                <button
                  type="button"
                  onClick={() => { setModalConsultar(true); setErro(''); }}
                  className="inline-flex items-center gap-2 bg-emerald-600 text-white px-6 py-2 rounded-lg hover:bg-emerald-700 transition text-sm"
                >
                  <Search size={16} /> Consultar NF-e
                </button>
              </div>
              <p className="text-xs muted mt-3">Consultar baixa o XML direto da SEFAZ pela chave de acesso (44 digitos).</p>
            </>
          )}
        </div>
      )}

      {nfe && (
        <div className="space-y-2">
          {/* Fornecedor */}
          <div className="rounded-lg border px-3 py-2" style={{ borderColor: 'var(--input-border)', backgroundColor: 'var(--card-bg)' }}>
            <h2 className="font-bold mb-1 text-sm">Fornecedor</h2>
            <div className="flex items-center gap-6 text-sm flex-wrap">
              <div>
                <span className="text-xs muted block">Razao Social</span>
                <span className="font-bold">{nfe.emitente.nome}</span>
              </div>
              <div>
                <span className="text-xs muted block">CNPJ</span>
                <span className="font-medium">{nfe.emitente.cnpj}</span>
              </div>
              <div>
                <span className="text-xs muted block">UF (XML)</span>
                {(() => {
                  const uf = String(nfe.emitente?.endereco?.uf || '').trim().toUpperCase();
                  const cls = uf === 'GO' ? 'text-green-700' : uf ? 'text-orange-700' : 'opacity-30';
                  return (
                    <span className={`font-bold ${cls}`} title="UF do emitente lida do XML. Determina se DIFAL e calculado (≠ GO).">
                      {uf ? `[${uf}]` : '— vazio —'}
                    </span>
                  );
                })()}
              </div>
              {(() => {
                const vST = itensVinculados.reduce((s, i) => s + (parseFloat(String(i.valor_icms_st)) || 0), 0);
                if (vST <= 0) return null;
                return (
                  <div>
                    <span className="text-xs muted block">ICMS-ST</span>
                    <button
                      type="button"
                      onClick={() => setStEmbutido(v => !v)}
                      className={`text-xs font-bold px-2 py-0.5 rounded transition cursor-pointer ${stEmbutido ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' : 'bg-orange-100 text-orange-700 hover:bg-orange-200'}`}
                      title={stEmbutido ? 'ST embutido no V.Unit (nao soma no custo). Clique pra trocar.' : 'ST cobrado por fora (somado ao custo). Clique pra trocar.'}
                    >
                      {stEmbutido ? '✓ Embutido no V.Unit' : '+ Por fora (soma)'}
                    </button>
                  </div>
                );
              })()}
              {fornecedorVinculado ? (
                <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 px-3 py-2 rounded-lg">
                  <CheckCircle size={16} />
                  Vinculado: <strong>
                    {fornecedorVinculado.nome_fantasia || fornecedorVinculado.razao_social}
                    {fornecedorVinculado.filial_apelido ? ` · ${fornecedorVinculado.filial_apelido}` : ''}
                  </strong>
                  <button onClick={() => setFornecedorVinculado(null)} className="ml-2 text-xs text-red-600 hover:text-red-800 hover:underline transition">
                    Desvincular
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 px-3 py-2 rounded-lg">
                  <AlertTriangle size={16} />
                  Nao vinculado
                  <button onClick={() => setModalVincularForn(true)} className="flex items-center gap-1 bg-green-600 text-white px-3 py-1 rounded-lg hover:bg-green-700 transition text-xs font-medium ml-2">
                    <Link2 size={14} /> Vincular
                  </button>
                  <button onClick={() => setModalFornecedor(true)} className="flex items-center gap-1 bg-blue-600 text-white px-3 py-1 rounded-lg hover:bg-blue-700 transition text-xs font-medium">
                    <Plus size={14} /> Cadastrar
                  </button>
                </div>
              )}
              {/* Cotacao de origem (0356) — OPCIONAL, nasce vazia: so encurta a lupa e mostra o que
                  foi pedido. Fornecedor sem cotacao nenhuma ve o motivo, nao um campo mudo. */}
              {fornecedorVinculado && (
                <div style={{ minWidth: 260 }}>
                  <span className="text-xs muted block">Cotacao de origem (opcional)</span>
                  {cotacoesForn.length === 0 ? (
                    <span className="text-xs muted italic">nenhuma cotacao com este fornecedor</span>
                  ) : (
                    <div data-cotacao-origem="1">
                      <ComboBox
                        value={cotacaoOrigemId}
                        onChange={v => setCotacaoOrigemId(v === '' || v == null ? null : Number(v))}
                        opcoes={cotacoesForn.map(c => ({
                          value: c.id,
                          // SO numero + Observacao (pedido dele, 31/10/2026, com print: "exiba so estas
                          // informacoes, as outras pode tirar") — data, itens e "comprado aqui" sairam.
                          // A Observacao e' o nome que ele da a cotacao, e a busca do combo acha por ela.
                          label: `${c.numero}${c.observacoes?.trim() ? ` · ${c.observacoes.trim()}` : ''}`,
                        }))}
                        placeholder="Escolha a cotacao, se houver..."
                      />
                    </div>
                  )}
                  {cotacaoOrigemId && erroCotacao && (
                    <span className="text-xs block" style={{ color: '#b91c1c' }}>{erroCotacao}</span>
                  )}
                  {cotacaoOrigemId && !erroCotacao && itensCotacao.length > 0 && (() => {
                    // O contador NOMEIA o que falta (31/10/2026: "ainda aparece 5 de 6" — so o numero
                    // nao dizia qual item nem o que fazer).
                    const faltam = itensCotacao.filter(c => linhaQueUsaItemCotacao(c) == null);
                    return (
                      <span className="text-[11px] muted block" style={{ maxWidth: 460 }}>
                        {contar(itensCotacao.length - faltam.length, 'item ligado', 'itens ligados')} de {itensCotacao.length} cotados
                        {faltam.length > 0 && (
                          <> — falta: <strong>{faltam.map(c => `${c.novo_produto?.codigo ?? c.produto?.codigo_interno ?? ''} ${c.descricao ?? ''}`.trim()).join('; ')}</strong>. Abra a lupa da linha desse item na nota e escolha-o em "Da cotacao".</>
                        )}
                      </span>
                    );
                  })()}
                </div>
              )}
              <div className="ml-auto flex items-center gap-4">
                <div>
                  <span className="text-xs muted block">Total da Nota Fiscal</span>
                  <div className="flex items-center gap-1">
                    <span className="text-xs muted font-medium">R$</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={totalNotaFiscal}
                      onChange={e => setTotalNotaFiscal(e.target.value)}
                      onBlur={e => {
                        const raw = e.target.value.replace(/\./g, '').replace(',', '.');
                        const num = parseFloat(raw);
                        if (!isNaN(num)) setTotalNotaFiscal(formatMoedaSemSimbolo(num));
                      }}
                      className="w-32 px-2 py-1.5 border border-orange-300 bg-orange-50 rounded text-sm font-bold text-right"
                      title="Valor total pago na nota (incluindo impostos, frete, etc). Edite se necessario."
                    />
                  </div>
                </div>
                <div>
                  <span className="text-xs muted block">Outras despesas</span>
                  <div className="flex items-center gap-1">
                    <span className="text-xs muted font-medium">R$</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={outrasDespesas}
                      onChange={e => setOutrasDespesas(e.target.value)}
                      onBlur={e => {
                        const raw = e.target.value.replace(/\./g, '').replace(',', '.');
                        const num = parseFloat(raw);
                        if (!isNaN(num) && num > 0) setOutrasDespesas(formatMoedaSemSimbolo(num));
                        else setOutrasDespesas('');
                      }}
                      placeholder="0,00"
                      className="w-28 px-2 py-1.5 border rounded text-sm font-bold text-right"
                      style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--input-border)' }}
                      title="Despesas extras fora da nota (taxa boleto, frete por fora, seguro). SOMA no CP e no custo dos produtos."
                    />
                  </div>
                </div>
                <div>
                  <span className="text-xs muted block">Total da entrada</span>
                  <div className="flex items-center gap-1">
                    <span className="text-xs muted font-medium">R$</span>
                    <div className="w-32 px-2 py-1.5 border rounded text-sm font-bold text-right" style={{ backgroundColor: '#fde68a', borderColor: '#ca8a04', color: '#000' }}>
                      {formatMoedaSemSimbolo(parseTotalNF() + parseOutrasDespesas())}
                    </div>
                  </div>
                </div>
                <div>
                  <span className="text-xs muted block">Credito do fornecedor</span>
                  <div className="flex items-center gap-1">
                    <span className="text-xs muted font-medium">R$</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={creditoFornecedor}
                      onChange={e => setCreditoFornecedor(e.target.value)}
                      onBlur={e => {
                        const raw = e.target.value.replace(/\./g, '').replace(',', '.');
                        const num = parseFloat(raw);
                        if (!isNaN(num) && num > 0) setCreditoFornecedor(formatMoedaSemSimbolo(num));
                        else setCreditoFornecedor('');
                      }}
                      placeholder="0,00"
                      className="w-28 px-2 py-1.5 border rounded text-sm font-bold text-right"
                      style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--input-border)' }}
                      title="Credito de fidelidade concedido pelo fornecedor. SUBTRAI do CP/boleto mas NAO afeta o custo dos produtos."
                    />
                  </div>
                  {parseCreditoFornecedor() > 0 && (
                    <div className="text-[10px] mt-0.5" style={{ color: '#15803d' }}>
                      Boleto: {formatMoedaSemSimbolo((parseTotalNF() || nfe?.valor_total || 0) + parseOutrasDespesas() - parseCreditoFornecedor())}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Banner anomalias */}
          {(() => {
            const comAnom = itensVinculados.map(it => detectaAnomalia(it)).filter(Boolean) as NonNullable<ReturnType<typeof detectaAnomalia>>[];
            const criticos = comAnom.filter(a => a.severidade === 'critico').length;
            const avisos = comAnom.length - criticos;
            const sugeridos = itensVinculados.filter(it => {
              const a = detectaAnomalia(it);
              return a && a.qt_emb_sugerido != null && a.qt_emb_sugerido !== (it.qtd_embalagem ?? 1);
            }).length;
            if (comAnom.length === 0) return null;
            return (
              <div className="rounded-lg border px-3 py-1.5" style={{ backgroundColor: '#fde68a', color: '#000', borderColor: '#f59e0b' }}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 text-sm">
                    <AlertTriangle size={18} />
                    <span className="font-semibold">
                      {criticos > 0 && <>{criticos} item{criticos > 1 ? 's' : ''} com variacao critica</>}
                      {criticos > 0 && avisos > 0 && ' · '}
                      {avisos > 0 && <>{avisos} aviso{avisos > 1 ? 's' : ''} de unidade</>}
                    </span>
                    <span className="text-xs">— confira as linhas marcadas em laranja/vermelho antes de confirmar.</span>
                  </div>
                  {sugeridos > 0 && (
                    <button
                      onClick={() => {
                        const novo = itensVinculados.map(it => {
                          const a = detectaAnomalia(it);
                          if (a && a.qt_emb_sugerido != null) {
                            return { ...it, qtd_embalagem: a.qt_emb_sugerido, _qtd_emb_str: String(a.qt_emb_sugerido).replace('.', ',') };
                          }
                          return it;
                        });
                        setItensVinculados(novo);
                      }}
                      className="text-xs font-bold bg-white border px-3 py-1 rounded hover:bg-gray-50 transition"
                      title="Aplica Qt.Emb sugerido em todos os itens com unidade divergente"
                    >
                      Aplicar Qt.Emb sugerido em {sugeridos} item{sugeridos > 1 ? 's' : ''}
                    </button>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Faixa do CATALOGO (DA-598) — so acende quando ha decisao pendente. Contador que    */}
          {/* aparece sempre vira paisagem, e ai para de significar alguma coisa quando importa. */}
          {pendenciasCat.length > 0 && (
            <div className="rounded-lg border px-3 py-1.5" style={{ backgroundColor: '#f97316', color: '#000', borderColor: '#c2410c' }}>
              <div className="flex items-center gap-2 text-sm">
                <AlertTriangle size={18} />
                <span className="font-semibold">
                  {contar(pendenciasCat.length, 'item')} com cadastro neste fornecedor sob OUTRO codigo ({rotulosPendenciasCat})
                </span>
                <span className="text-xs">
                  — na linha <b>catalogo</b> de cada um, diga se e o mesmo item ou se e para criar novo. Sem isso a importacao cria uma segunda linha no catalogo.
                </span>
              </div>
            </div>
          )}

          {duplicadasCat > 0 && (
            <div className="rounded-lg border px-3 py-1.5" style={{ backgroundColor: '#fde68a', color: '#000', borderColor: '#f59e0b' }}>
              <div className="flex items-center gap-2 text-sm">
                <AlertTriangle size={18} />
                <span className="font-semibold">
                  {contar(duplicadasCat, 'produto')} desta nota com mais de uma oferta no catalogo deste fornecedor
                </span>
                <span className="text-xs">
                  — nas linhas <b>catalogo</b> de cada um, marque de qual delas e esta compra. Marcar a outra move o codigo da nota pra ela e desfaz a duplicata.
                </span>
              </div>
            </div>
          )}

          {/* Itens */}
          <div className="rounded-lg border" style={{ borderColor: 'var(--input-border)', backgroundColor: 'var(--card-bg)' }}>
            <div className="px-3 py-2 border-b flex items-center justify-between" style={{ borderColor: 'var(--input-border)' }}>
              <h2 className="font-bold flex items-center gap-2 text-sm">
                <Package size={16} className="text-blue-600" />
                Itens ({itensVinculados.length})
              </h2>
              <div className="flex items-center gap-3">
                {totalNaoVinculados > 0 && (
                  <button onClick={criarTodosProdutos} disabled={salvando} className="flex items-center gap-1 bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 transition text-xs font-medium disabled:opacity-50">
                    <Plus size={14} /> Criar {totalNaoVinculados} produto{totalNaoVinculados > 1 ? 's' : ''} nao vinculado{totalNaoVinculados > 1 ? 's' : ''}
                  </button>
                )}
                <span className={`text-sm font-medium ${totalVinculados === itensVinculados.length ? 'text-green-600' : 'text-amber-600'}`}>
                  {totalVinculados}/{itensVinculados.length} vinculados
                </span>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse [&_td]:border [&_td]:border-gray-300 [&_th]:border [&_th]:border-gray-300">
                <thead>
                  <tr className="border-b text-[11px]" style={{ backgroundColor: 'var(--grid-header-bg)', color: 'var(--grid-header-fg)' }}>
                    <th className="text-center px-1 py-1.5 font-semibold w-7">#</th>
                    <th className="text-center px-0.5 py-1.5 font-semibold w-10">Foto</th>
                    <th className="text-center px-1 py-1.5 font-semibold w-20">Cod.</th>
                    <th className="text-left px-1 py-1.5 font-semibold" style={{ width: '18%' }}>Produto Vinculado</th>
                    <th className="text-right px-1 py-1.5 font-semibold w-14" title="Estoque atual do produto antes desta nota">Estoque</th>
                    <th className="text-center px-1 py-1.5 font-semibold w-28" title="Prateleira que vai receber esta mercadoria. Ja vem com o lugar que o sistema usaria (o mais fundo, nunca a vitrine) — ajuste se for outro.">Localizacao</th>
                    <th className="text-left px-1 py-1.5 font-semibold w-16">Cod.F.</th>
                    <th className="text-left px-1 py-1.5 font-semibold" style={{ width: '24%' }}>Descricao NF</th>
                    <th className="text-left px-1 py-1.5 font-semibold w-40" title="Sao DOIS destinos. Na linha do item: a marca do CADASTRO do produto — vem sugerida da nota/catalogo so quando o cadastro esta sem marca, e voce pode trocar. Na linha do catalogo: a marca daquela linha, que fica gravada no Catalogo do fornecedor ao confirmar. Um nao mexe no outro.">Marca</th>
                    <th className="text-right px-1 py-1.5 font-semibold w-12">Qtd.</th>
                    <th className="text-right px-1 py-1.5 font-semibold w-16">V.Unit.</th>
                    <th className="text-center px-1 py-1.5 font-semibold w-12" title="Unidade do XML">Un NF</th>
                    <th className="text-center px-1 py-1.5 font-semibold w-24" title="Embalagem do cadastro">Emb.</th>
                    <th className="text-center px-1 py-1.5 font-semibold w-14">Qt.Emb</th>
                    <th className="text-right px-1 py-1.5 font-semibold w-16" title="Valor da NF dividido pela quantidade real (sem outras despesas)">V.NF/un</th>
                    <th className="text-right px-1 py-1.5 font-semibold w-14">ST</th>
                    <th className="text-right px-1 py-1.5 font-semibold w-12">Aliq.</th>
                    <th className="text-right px-1 py-1.5 font-semibold w-14" title="IPI cobrado pelo fornecedor (do XML) — soma no custo">IPI</th>
                    <th className="text-right px-1 py-1.5 font-semibold w-14">DIFAL</th>
                    <th className="text-right px-1 py-1.5 font-semibold w-14" title="Outras Despesas — rateio proporcional">OD</th>
                    <th className="text-right px-1 py-1.5 font-semibold w-16" style={{ backgroundColor: '#dcfce7', color: '#15803d' }}>Custo</th>
                  </tr>
                </thead>
                <tbody>
                  {itensVinculados.map((item, idx) => {
                    const anomalia = detectaAnomalia(item);
                    const anomUnidade = anomalia?.detalhes?.find((a: any) => a.tipo === 'unidade_divergente');
                    const anomCusto = anomalia?.detalhes?.find((a: any) => a.tipo === 'variacao_custo');
                    const destino = destinosCat[idx] ?? null;
                    const marcaItem = marcaDoProdutoExibida(idx);
                    return (
                      <Fragment key={idx}>
                      <tr
                        className="text-[11px]"
                        onClick={(e) => {
                          // Toggle so se o click foi numa celula "vazia" — nao em input/select/button/etc
                          const tag = (e.target as HTMLElement).tagName;
                          if (['INPUT', 'SELECT', 'BUTTON', 'TEXTAREA', 'A', 'IMG', 'SVG', 'PATH'].includes(tag)) return;
                          if ((e.target as HTMLElement).closest('button, input, select, textarea, a, [role="button"]')) return;
                          setLinhasMarcadas((prev) => {
                            const next = new Set(prev);
                            if (next.has(idx)) next.delete(idx); else next.add(idx);
                            return next;
                          });
                        }}
                        style={{
                          backgroundColor: linhasMarcadas.has(idx)
                            ? '#fde68a'
                            : (idx % 2 === 1 ? 'var(--grid-row-alt-bg)' : 'var(--grid-row-bg)'),
                          color: linhasMarcadas.has(idx) ? '#000' : undefined,
                          cursor: 'pointer',
                          // Cada item ocupa varias linhas (a dele + as do catalogo) e a divisa entre
                          // um registro e o proximo tinha a mesma espessura da grade interna. Vai no
                          // TOPO: assim ela fecha o bloco anterior inteiro em vez de cortar este ao
                          // meio. ⚠️ Aqui e' style, NAO classe: `[&>td]:border-t-[3px]` tem a mesma
                          // especificidade (0,1,1) do `[&_td]:border` da <table> e perde por vir
                          // antes no CSS — subiu em prod desenhando nada (medido no CSS do build).
                          // No <tr> com border-collapse a colisao se resolve pela LARGURA: 3 > 1.
                          borderTop: '3px solid var(--grid-header-bg)',
                        }}
                      >
                        <td className="px-1 py-1.5 text-center muted">{item.numero_item}</td>
                        <td className="px-0.5 py-1 text-center">
                          {item.produto_vinculado?.foto_url ? (
                            <img src={item.produto_vinculado.foto_url} alt="" className="w-8 h-8 object-contain rounded mx-auto" />
                          ) : (
                            <div className="w-8 h-8 rounded flex items-center justify-center mx-auto" style={{ backgroundColor: 'var(--input-bg)' }}>
                              <Package size={14} className="opacity-30" />
                            </div>
                          )}
                        </td>
                        <td className="px-1 py-1.5">
                          <div className="flex items-center justify-center gap-0.5">
                            <input
                              key={`cod-${idx}-${item.produto_id || 'none'}`}
                              type="text"
                              defaultValue={item.produto_vinculado?.codigo_interno || ''}
                              placeholder="Codigo"
                              className={`w-14 px-1 py-0.5 border rounded text-[11px] text-center font-bold ${item.produto_vinculado ? 'border-green-300 bg-green-50 text-green-800' : ''}`}
                              style={!item.produto_vinculado ? { backgroundColor: 'var(--input-bg)', borderColor: 'var(--input-border)' } : undefined}
                              onKeyDown={e => { if (e.key === 'Enter') aplicarCodigo(idx, (e.target as HTMLInputElement).value.trim()); }}
                              onBlur={e => aplicarCodigo(idx, e.target.value.trim())}
                            />
                            <button
                              onClick={() => { setModalBuscaProd({ idx }); setBuscaProd(''); setResultadosProd([]); }}
                              className="p-1 hover:text-blue-600 hover:bg-blue-50 rounded transition"
                              title="Buscar produto"
                            >
                              <Search size={14} />
                            </button>
                          </div>
                          {/* Combo "Da cotacao" da linha (31/10/2026, print dele na coluna Cod.). So com
                              cotacao de origem informada; a lista abre larga, a coluna e' estreita. */}
                          {itensCotacao.length > 0 && (
                            // Caixa do tamanho do codigo e lista larga (print dele, 31/10/2026: "o combo deve
                            // ficar deste tamanho, e a lista do mesmo tamanho que esta"). A largura FIXA no
                            // wrapper e' o que impede o rotulo de alargar a coluna.
                            <div className="mt-1 mx-auto combo-acao" style={{ width: 84 }} data-cotacao-linha={idx} title="Escolha o item da cotacao que esta linha da nota representa">
                              <ComboBox
                                value={itemCotacaoDaLinha(item)?.cotacao_item_id ?? null}
                                onChange={v => escolherItemCotacaoNaLinha(idx, v)}
                                opcoes={opcoesItemCotacao}
                                placeholder="Cotacao..."
                                className="!py-0.5 !px-1 !text-[11px] !min-h-0"
                                larguraMinLista={500}
                              />
                            </div>
                          )}
                        </td>
                        <td className="px-1 py-1.5">
                          {item.produto_vinculado ? (
                            <div className="flex flex-col gap-0.5">
                              <div className="flex items-start gap-0.5">
                                <CheckCircle size={11} className="text-green-600 flex-shrink-0 mt-[2px]" />
                                <span className="flex-1 min-w-0 text-[11px] text-green-700 font-medium whitespace-normal break-words leading-tight">{item.produto_vinculado.descricao}</span>
                                <button onClick={() => desvincularProduto(idx)} className="text-red-400 hover:text-red-600 p-0.5 flex-shrink-0 mt-[1px]" title="Desvincular">
                                  <X size={11} />
                                </button>
                              </div>
                              {/* NP escolhido numa linha JA ligada a produto (31/10/2026, BETEL: o terminal
                                  virou o 1963 mas o NP00035 ficou na prateleira). O POST aposenta com o
                                  produto da linha; sem este selo a escolha era invisivel. */}
                              {item.novo_produto_id && (
                                <span className="inline-flex items-center gap-1 text-[10px] font-bold rounded px-1.5 py-0.5 w-fit"
                                  style={{ backgroundColor: '#c4b5fd', color: '#000' }}
                                  title="Ao salvar, este item sai de Novos Produtos (fica ligado a este produto)">
                                  {item.novo_produto_codigo || 'NOVO'} sai de Novos Produtos
                                  <button type="button" onClick={() => desvincularNovoProduto(idx)}
                                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#000', lineHeight: 1 }}
                                    title="Desfazer">×</button>
                                </span>
                              )}
                              {/* Uso/Consumo continua visivel/editavel DEPOIS de vincular: grava na ficha do produto. */}
                              <label
                                className="flex items-center gap-1 text-[10px] cursor-pointer"
                                title="Item de uso/consumo da loja (nao aparece na venda). Grava direto na ficha deste produto."
                                style={item.produto_vinculado.uso_consumo ? { backgroundColor: '#fde68a', color: '#000', fontWeight: 700, borderRadius: 4, padding: '0 3px', width: 'fit-content' } : { color: 'var(--text-muted)' }}
                              >
                                <input
                                  type="checkbox"
                                  checked={!!item.produto_vinculado.uso_consumo}
                                  onChange={() => alternarUsoConsumoVinculado(idx)}
                                />
                                Uso/Consumo
                              </label>
                              {/* Sem Unidade de Compra o sistema nao sabe se ele vem em pacote, e a
                                  cotacao contaria unidades (regra dele, 30/10/2026). Grava na ficha. */}
                              {faltaCompra(item.produto_vinculado) ? (
                                <button
                                  type="button"
                                  data-sem-compra="1"
                                  onClick={() => setModalEmbCompra({
                                    produtoId: item.produto_vinculado!.id,
                                    codigo: item.produto_vinculado!.codigo_interno,
                                    descricao: item.produto_vinculado!.descricao,
                                  })}
                                  className="btn-primario text-[10px] font-semibold px-1.5 py-0.5 rounded w-fit"
                                  title="O cadastro deste produto nao diz como ele e comprado"
                                >
                                  Informar Unidade de Compra
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => setModalEmbCompra({
                                    produtoId: item.produto_vinculado!.id,
                                    codigo: item.produto_vinculado!.codigo_interno,
                                    descricao: item.produto_vinculado!.descricao,
                                  })}
                                  className="text-[10px] muted underline w-fit text-left"
                                  title="Unidade de Compra do cadastro — clique para corrigir"
                                >
                                  Compra: {String(item.produto_vinculado.unidade_compra).toUpperCase()} / {formatQtdBr(Number(item.produto_vinculado.qtd_embalagem_padrao))}
                                </button>
                              )}
                              <CotadoDaLinha c={itemCotacaoDaLinha(item)} />
                            </div>
                          ) : (
                            <div className="flex flex-col gap-0.5">
                              {/* O selo da prateleira: sem ele, o casamento com Novos Produtos
                                  seria invisivel e o operador nao saberia o que a nota vai
                                  aposentar. Traz o X pra desfazer — escolha sem volta na tela e
                                  beco sem saida. */}
                              {item.novo_produto_id && (
                                <span className="inline-flex items-center gap-1 text-[10px] font-bold rounded px-1.5 py-0.5 w-fit"
                                  style={{ backgroundColor: '#c4b5fd', color: '#000' }}
                                  title="Ao salvar, este item sai de Novos Produtos e nasce no Catalogo deste fornecedor">
                                  {item.novo_produto_codigo || 'NOVO'}
                                  <button type="button" onClick={() => desvincularNovoProduto(idx)}
                                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#000', lineHeight: 1 }}
                                    title="Desfazer">×</button>
                                </span>
                              )}
                              <CotadoDaLinha c={itemCotacaoDaLinha(item)} />
                              <div className="flex items-center gap-0.5">
                                <span className="text-[11px] muted italic">Nao vinculado</span>
                                <button
                                  onClick={() => abrirModalCriarProduto(idx, item)}
                                  className="flex items-center gap-0.5 bg-blue-600 hover:bg-blue-700 text-white px-1.5 py-0.5 rounded text-[11px] font-medium transition"
                                  title="Criar novo produto"
                                >
                                  <Plus size={11} /> Criar
                                </button>
                              </div>
                              {/* Uso/Consumo: o produto criado por esta linha nasce escondido da venda. */}
                              <label
                                className="flex items-center gap-1 text-[10px] cursor-pointer"
                                title="Item de uso/consumo da loja (nao aparece na venda). Vale so pro produto CRIADO nesta linha."
                                style={item.uso_consumo ? { backgroundColor: '#fde68a', color: '#000', fontWeight: 700, borderRadius: 4, padding: '0 3px', width: 'fit-content' } : { color: 'var(--text-muted)' }}
                              >
                                <input
                                  type="checkbox"
                                  checked={!!item.uso_consumo}
                                  onChange={(e) => {
                                    const novo = [...itensVinculados];
                                    novo[idx] = { ...novo[idx]!, uso_consumo: e.target.checked };
                                    setItensVinculados(novo);
                                  }}
                                />
                                Uso/Consumo
                              </label>
                            </div>
                          )}
                        </td>
                        <td className="px-1 py-1.5 text-[11px] text-right" title="Estoque atual do produto antes desta nota">
                          {item.produto_vinculado
                            ? <span className="font-semibold">{Number(item.produto_vinculado.estoque_atual || 0).toLocaleString('pt-BR', { maximumFractionDigits: 3 })}</span>
                            : <span className="muted">—</span>}
                        </td>
                        <td
                          className="px-1 py-1.5"
                          // Alvo do "ver o primeiro" — so na PRIMEIRA linha sem prateleira ([DA-618]),
                          // igual ao que o portao do catalogo ja faz com `data-pendencia-catalogo`.
                          data-sem-lugar={semLugar[0] === idx ? '1' : undefined}
                        >
                          {item.produto_id ? (
                            <LocalDaEntrada
                              value={item.localizacao_id ?? null}
                              opcoes={opcoesLocal}
                              lugares={item._lugares}
                              faltando={semLugar.includes(idx)}
                              onChange={(v) => {
                                const novo = [...itensVinculados];
                                novo[idx] = { ...novo[idx]!, localizacao_id: v };
                                setItensVinculados(novo);
                              }}
                            />
                          ) : (
                            <span className="muted text-[11px] block text-center" title="Vincule ou crie o produto primeiro">—</span>
                          )}
                        </td>
                        <td className="px-1 py-1.5 text-[11px] muted">{item.codigo_fornecedor}</td>
                        <td className="px-1 py-1.5 font-medium align-top">
                          <span className="block text-[11px] whitespace-normal break-words leading-tight" title={item.descricao}>{item.descricao}</span>
                        </td>
                        {/* A Marca do CADASTRO do produto (05/09/2026). Editavel, e independente do
                            combo da linha do catalogo logo abaixo — sao dois destinos diferentes
                            (`produtos.marca_id` aqui, `catalogo_item.marca` la). Quando o cadastro
                            esta sem marca, a celula PROPOE a do catalogo como valor de partida; com
                            marca no cadastro, nada e' proposto por cima ([DA-307] pelo mesmo motivo).
                            A etiqueta embaixo diz de onde o valor saiu: sem ela, "ja gravada" e
                            "estou sugerindo" ficam iguais na tela, e so uma delas grava algo novo. */}
                        {(() => {
                          const semProduto = !item.produto_id;
                          const travado = semProduto || !atualizarEstoque;
                          const porque = semProduto
                            ? 'Vincule um produto pra definir a marca do cadastro'
                            : 'Marque "Atualizar estoque e preco de custo dos produtos vinculados" pra gravar no cadastro';
                          return (
                            <td
                              className="px-1 py-1.5 text-[11px]"
                              title={travado
                                ? porque
                                : marcaItem
                                  ? (marcaItem.origem === 'cadastro'
                                      ? `${marcaItem.texto} — ja e a marca do cadastro deste produto`
                                      : marcaItem.origem === 'espelho'
                                        ? `${marcaItem.texto} — sugerida pela nota/catalogo; o cadastro esta sem marca e vai receber esta`
                                        : `${marcaItem.texto} — escolhida por voce; vai pro cadastro do produto`)
                                  : 'O cadastro deste produto ficara sem marca'}
                            >
                              <div className="flex items-center gap-1" style={{ maxWidth: 150 }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <ComboBox
                                    value={marcaItem?.texto || null}
                                    onChange={v => editarMarcaProduto(idx, v == null ? '' : String(v))}
                                    opcoes={opcoesMarca}
                                    placeholder="marca..."
                                    disabled={travado}
                                  />
                                </div>
                                <button
                                  type="button"
                                  disabled={travado}
                                  onClick={() => setMarcaNovaAlvo({ idx, idLinha: null, produto: true })}
                                  title="Cadastrar uma marca nova"
                                  className="rounded border px-1 py-0.5 disabled:opacity-40"
                                  style={{ borderColor: 'var(--input-border)', lineHeight: 1 }}
                                >
                                  <Plus size={11} />
                                </button>
                              </div>
                              {marcaItem && !travado && (
                                <span className="muted block text-[9px] leading-tight mt-0.5">
                                  {marcaItem.origem === 'cadastro'
                                    ? 'do cadastro'
                                    : marcaItem.origem === 'espelho' ? 'sugerida' : 'sua escolha'}
                                </span>
                              )}
                            </td>
                          );
                        })()}
                        <td className="px-1 py-1.5 text-right text-[11px]">{Number(item.quantidade).toLocaleString('pt-BR')}</td>
                        <td className="px-1 py-1.5 text-right text-[11px]">{formatMoedaSemSimbolo(item.valor_unitario)}</td>
                        <td
                          className="px-1 py-1.5 text-center text-[11px] font-bold"
                          style={anomUnidade ? { backgroundColor: '#fde68a', color: '#000' } : { color: '#6b7280' }}
                          title={anomUnidade ? anomUnidade.msg : undefined}
                        >
                          {item.unidade || '—'}
                        </td>
                        <td className="px-1 py-1.5 text-center">
                          <select
                            value={String(item.emb_venda_id || '')}
                            onChange={e => {
                              const novo = [...itensVinculados];
                              const und = unidades.find(u => String(u.id) === e.target.value);
                              novo[idx] = { ...novo[idx]!, emb_venda_id: e.target.value ? parseInt(e.target.value) : null, emb_venda_sigla: und?.sigla || '' };
                              setItensVinculados(novo);
                            }}
                            className="w-20 px-0.5 py-0.5 border rounded text-[11px]"
                            style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--input-border)' }}
                          >
                            <option value="">—</option>
                            {unidades.filter(u => u.ativo !== false || u.id === item.emb_venda_id).map(u => (
                              <option key={u.id} value={String(u.id)}>{u.sigla} — {u.nome}{u.ativo === false ? ' (inativa)' : ''}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-1 py-1.5 text-center">
                          <input
                            type="text"
                            inputMode="decimal"
                            value={item._qtd_emb_str ?? String(item.qtd_embalagem ?? 1).replace('.', ',')}
                            onChange={e => {
                              const raw = e.target.value;
                              const novo = [...itensVinculados];
                              novo[idx] = { ...novo[idx]!, _qtd_emb_str: raw };
                              setItensVinculados(novo);
                            }}
                            onBlur={e => {
                              const raw = String(e.target.value).replace(/\./g, '').replace(',', '.');
                              const v = parseFloat(raw);
                              const final = Number.isFinite(v) && v > 0 ? v : 1;
                              const novo = [...itensVinculados];
                              novo[idx] = { ...novo[idx]!, qtd_embalagem: final, _qtd_emb_str: String(final).replace('.', ',') };
                              setItensVinculados(novo);
                            }}
                            className={`w-14 px-1 py-0.5 border rounded text-[11px] text-center font-bold ${(item.qtd_embalagem != null && item.qtd_embalagem !== 1) ? 'border-orange-400 bg-orange-50 text-orange-800' : ''}`}
                            style={!(item.qtd_embalagem != null && item.qtd_embalagem !== 1) ? { backgroundColor: 'var(--input-bg)', borderColor: 'var(--input-border)' } : undefined}
                            title="Multiplicador da quantidade da NF para chegar no estoque interno. Use < 1 quando NF vem em unidade menor que o cadastro"
                          />
                        </td>
                        <td className="px-1 py-1.5 text-right text-[11px] font-medium">
                          {(() => {
                            const qtdEmb = item.qtd_embalagem ?? 1;
                            const qtdTotal = item.quantidade * qtdEmb;
                            const valorFrac = qtdTotal > 0 ? item.valor_total / qtdTotal : 0;
                            return formatMoedaSemSimbolo(valorFrac);
                          })()}
                        </td>
                        <td className="px-1 py-1.5 text-right text-[11px] font-medium">
                          {(() => {
                            const st = item.valor_icms_st || 0;
                            if (st <= 0) return <span className="opacity-30">—</span>;
                            const qtdEmb = item.qtd_embalagem ?? 1;
                            const qtdTotal = item.quantidade * qtdEmb;
                            const stUnit = qtdTotal > 0 ? st / qtdTotal : st;
                            return (
                              <span className={item.st_calculado ? 'text-blue-700' : 'text-orange-700'} title={item.st_calculado ? 'Calculado via tabela NCM-ST' : 'Valor do XML'}>
                                {formatMoedaSemSimbolo(stUnit)}{item.st_calculado ? '*' : ''}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="px-1 py-1.5 text-right text-[11px] font-medium">
                          {item.aliquota_icms > 0 ? `${Number(item.aliquota_icms).toLocaleString('pt-BR')}%` : <span className="opacity-30">—</span>}
                        </td>
                        <td className="px-1 py-1.5 text-right text-[11px] font-medium">
                          {(() => {
                            const ipi = parseFloat(String(item.valor_ipi)) || 0;
                            if (ipi <= 0) return <span className="opacity-30">—</span>;
                            const qtdEmb = item.qtd_embalagem ?? 1;
                            const qtdTotal = item.quantidade * qtdEmb;
                            const ipiUnit = qtdTotal > 0 ? ipi / qtdTotal : ipi;
                            return <span className="text-amber-700" title={`Total no item: R$ ${formatMoedaSemSimbolo(ipi)} · ${formatMoedaSemSimbolo(ipiUnit)} por unidade`}>{formatMoedaSemSimbolo(ipiUnit)}</span>;
                          })()}
                        </td>
                        <td className="px-1 py-1.5 text-right text-[11px] font-medium">
                          {(() => {
                            const difal = calcDifal(item, nfe?.emitente?.endereco?.uf, nfe?.valor_frete || 0, nfe?.valor_produtos || 0);
                            if (difal <= 0) return <span className="opacity-30">—</span>;
                            const qtdEmb = item.qtd_embalagem ?? 1;
                            const qtdTotal = item.quantidade * qtdEmb;
                            const difalUnit = qtdTotal > 0 ? difal / qtdTotal : difal;
                            return <span className="text-purple-700" title={`Total no item: R$ ${formatMoedaSemSimbolo(difal)} · ${formatMoedaSemSimbolo(difalUnit)} por unidade`}>{formatMoedaSemSimbolo(difal)}</span>;
                          })()}
                        </td>
                        <td className="px-1 py-1.5 text-right text-[11px] font-medium">
                          {(() => {
                            const qtdEmb = item.qtd_embalagem ?? 1;
                            const qtdTotal = item.quantidade * qtdEmb;
                            if (qtdTotal <= 0) return <span className="opacity-30">—</span>;
                            const totalNF = (parseTotalNF() || 0) + parseOutrasDespesas();
                            const somaXml = nfe?.valor_produtos || 0;
                            const totalIPIAll = itensVinculados.reduce((s, it) => s + (parseFloat(String(it.valor_ipi)) || 0), 0);
                            const custosComp = totalNF - somaXml - totalIPIAll;
                            const fatorSh = (somaXml > 0 && custosComp > 0) ? custosComp / somaXml : 0;
                            const odTotal = item.valor_total * fatorSh;
                            if (odTotal <= 0) return <span className="opacity-30">—</span>;
                            const odUnit = odTotal / qtdTotal;
                            return <span className="text-blue-700" title={`Total no item: R$ ${formatMoedaSemSimbolo(odTotal)} · ${formatMoedaSemSimbolo(odUnit)} por unidade`}>{formatMoedaSemSimbolo(odTotal)}</span>;
                          })()}
                        </td>
                        <td
                          className="px-1 py-1.5 text-right text-[11px] font-bold"
                          style={anomCusto ? { backgroundColor: '#fde68a' } : { backgroundColor: 'rgb(240 253 244)' }}
                        >
                          {(() => {
                            const qtdEmb = item.qtd_embalagem ?? 1;
                            const qtdTotal = item.quantidade * qtdEmb;
                            if (qtdTotal <= 0) return '—';
                            const totalNF = (parseTotalNF() || 0) + parseOutrasDespesas();
                            const somaXml = nfe?.valor_produtos || 0;
                            const totalIPIAll = itensVinculados.reduce((s, it) => s + (parseFloat(String(it.valor_ipi)) || 0), 0);
                            const custosComp = totalNF - somaXml - totalIPIAll;
                            const fatorSh = (somaXml > 0 && custosComp > 0) ? custosComp / somaXml : 0;
                            const vnfUnit = item.valor_total / qtdTotal;
                            const odUnit = item.valor_total * fatorSh / qtdTotal;
                            const ipiUnit = (parseFloat(String(item.valor_ipi)) || 0) / qtdTotal;
                            const custoCompra = vnfUnit + odUnit + ipiUnit;
                            const difal = calcDifal(item, nfe?.emitente?.endereco?.uf, nfe?.valor_frete || 0, nfe?.valor_produtos || 0);
                            const stTotal = stEmbutido ? 0 : (item.valor_icms_st || 0);
                            const impostoUnit = (difal > 0 ? difal : stTotal) / qtdTotal;
                            const custoReal = custoCompra + impostoUnit;
                            const sigla = item.produto_vinculado?.unidade_compra || item.unidade || 'un';
                            const partes: string[] = [];
                            partes.push(`R$ ${formatMoedaSemSimbolo(vnfUnit)} (NF)`);
                            if (odUnit > 0) partes.push(`R$ ${formatMoedaSemSimbolo(odUnit)} (Out. desp.)`);
                            if (ipiUnit > 0) partes.push(`R$ ${formatMoedaSemSimbolo(ipiUnit)} (IPI)`);
                            if (impostoUnit > 0) partes.push(`R$ ${formatMoedaSemSimbolo(impostoUnit)} (${difal > 0 ? 'DIFAL' : 'ST'})`);
                            const tooltipComp = partes.join(' + ') + ` = R$ ${formatMoedaSemSimbolo(custoReal)}/${sigla}`;
                            const tooltipFull = anomCusto ? `${tooltipComp}\n⚠️ ${anomCusto.msg}` : tooltipComp;
                            return <span className="text-green-800" title={tooltipFull}>{formatMoedaSemSimbolo(custoReal)}</span>;
                          })()}
                        </td>
                      </tr>
                      {/* A LINHA DO CATALOGO (DA-598) — mesma grade, mesmas colunas: Cod.F., */}
                      {/* Descricao, Marca, Emb., Qt.Emb e preco caem embaixo dos da nota, entao a */}
                      {/* divergencia aparece na vertical sem ninguem comparar de cabeca.         */}
                      {(() => {
                        const escolhido = escolhaCat[idx];
                        const linhasCat = destino?.linhas ?? [];
                        const pendente = destino?.situacao === 'decidir' && escolhido === undefined;
                        const fundo = linhasMarcadas.has(idx)
                          ? '#fde68a'
                          : (idx % 2 === 1 ? 'var(--grid-row-alt-bg)' : 'var(--grid-row-bg)');
                        const celula = 'px-1 pb-1 text-[10px]';
                        const preto = linhasMarcadas.has(idx) ? '#000' : undefined;

                        // Qual linha esta marcada como "a compra e desta". Sem escolha do operador,
                        // vem marcada a que o codigo do XML ja alcanca — que e o que o sistema faria
                        // sozinho. Assim o estado inicial da tela NAO e uma pergunta em branco: e a
                        // resposta que o sistema daria, pronta pra ser corrigida.
                        // Mesma funcao que o campo de marca usa — duas copias da regra divergiriam,
                        // e a marca acabaria gravada numa linha diferente da que esta marcada.
                        const marcadaId = linhaMarcadaId(idx);

                        // Produto inedito naquele fornecedor NAO cai mais aqui: ele desce pro caminho
                        // normal e aparece como a unica opcao — "criar uma linha nova" — ja marcada.
                        // Toda linha da nota exibe entao a MESMA marcacao, e "sera criado" deixa de
                        // ser um texto cinza que o olho pula pra virar a caixa que os outros tem.
                        const aviso = !item.produto_id ? 'vincule o produto primeiro'
                          : !destino ? 'conferindo...'
                          : null;

                        if (erroPreviaCat || aviso) {
                          return (
                            <tr className="border-b text-[10px]" style={{ backgroundColor: fundo, color: preto }}>
                              <td className={celula} colSpan={3} style={{ textAlign: 'center' }}>
                                <span className="opacity-60 italic">catalogo</span>
                              </td>
                              <td className={celula} colSpan={18}>
                                {erroPreviaCat ? (
                                  // Fundo solido + letra preta, nunca cor de texto ([DA-597]).
                                  <span className="font-bold px-1.5 py-0.5 rounded"
                                    style={{ backgroundColor: '#fde68a', color: '#000' }}>
                                    nao consegui conferir o catalogo deste fornecedor
                                  </span>
                                ) : <span className="opacity-60">{aviso}</span>}
                              </td>
                            </tr>
                          );
                        }

                        // Uma linha por oferta que a empresa tem PRO NOSSO CODIGO INTERNO, mais a
                        // opcao de criar outra. O operador marca de qual delas e esta compra.
                        const opcoes: Array<CandidatoCatalogo | 'novo'> = [...linhasCat, 'novo'];
                        return opcoes.map((op, li) => {
                          const ultima = li === opcoes.length - 1;
                          const novo = op === 'novo';
                          const c = novo ? null : op as CandidatoCatalogo;
                          // Sem linha nenhuma no catalogo, "criar" e' o unico destino possivel — nasce
                          // marcada, porque perguntar o que nao tem alternativa e' so um clique a mais.
                          const marcada = novo
                            ? (escolhido === 'novo' || (linhasCat.length === 0 && escolhido === undefined))
                            : marcadaId === c!.id;
                          // Marcar uma linha que o codigo do XML NAO alcanca, havendo outra que
                          // alcanca, obriga a mover o codigo — e a linha de onde ele sai deixa de
                          // existir. E' a duplicata sendo desfeita, e tem que estar escrito.
                          const donoDoCodigo = linhasCat.find(o => o.casa_por_codigo) ?? null;
                          const vaiRemover = !!(marcada && c && !c.casa_por_codigo && donoDoCodigo);
                          // So na linha NOVA e so quando alguem DIGITOU aqui: sem edicao a celula
                          // espelha o item e nao ha divergencia nenhuma pra avisar.
                          const avisoDivergencia = novo && marcada
                            && item._cat_qtd_nova != null
                            && parseQtdEmbNova(item._cat_qtd_nova) !== (item.qtd_embalagem ?? 1);
                          return (
                            <tr
                              key={novo ? 'novo' : c!.id}
                              className={`text-[10px] ${ultima ? 'border-b' : ''}`}
                              data-pendencia-catalogo={pendente && li === 0 ? '1' : undefined}
                              style={{ backgroundColor: fundo, color: preto }}
                            >
                              <td className={celula}></td>
                              <td className={celula}></td>
                              <td className={`${celula} text-center`}>
                                {li === 0 && <span className="opacity-60 italic">catalogo</span>}
                              </td>
                              <td className={celula}>
                                <label className="flex items-start gap-1.5" style={{ cursor: 'pointer', lineHeight: 1.35 }}>
                                  <input
                                    type="checkbox"
                                    checked={marcada}
                                    onChange={() => setEscolhaCat(prev => ({
                                      ...prev,
                                      [idx]: novo ? 'novo' : c!.id,
                                    }))}
                                    style={{ marginTop: 1, flex: '0 0 auto' }}
                                    title="A compra desta nota se refere a esta linha do catalogo"
                                  />
                                  <span className="flex flex-col gap-0.5">
                                    <span className={marcada ? 'font-bold' : 'opacity-70'}>
                                      {novo
                                        ? 'criar uma linha nova para esta compra'
                                        : c!.casa_por_codigo
                                          ? 'a compra cai aqui (o codigo da nota e desta linha)'
                                          : 'a compra passa a cair aqui'}
                                    </span>
                                    {novo && linhasCat.length === 0 && (
                                      <span className="opacity-70">este fornecedor ainda nao tem este produto</span>
                                    )}
                                    {vaiRemover && (
                                      <span className="font-bold px-1.5 py-0.5 rounded self-start"
                                        style={{ backgroundColor: '#f97316', color: '#000', lineHeight: 1.3 }}>
                                        ao confirmar, o codigo {donoDoCodigo!.codigo_fornecedor} passa
                                        para esta linha e a linha dele e removida do catalogo
                                      </span>
                                    )}
                                    {marcada && !novo && c!.tem_alt && !c!.casa_por_codigo && (
                                      <span className="opacity-70">
                                        esta linha ja usa <b>{c!.codigo_fornecedor_alt}</b> como
                                        alternativo — troque no Catalogo antes
                                      </span>
                                    )}
                                  </span>
                                </label>
                                {/* A DIVERGENCIA QUE CUSTOU UMA NOTA INTEIRA (22/08/2026). Sao dois
                                    campos com o mesmo nome, na mesma coluna, um debaixo do outro: o
                                    Qt.Emb do ITEM decide ESTOQUE e CUSTO, o daqui descreve a oferta
                                    do catalogo. Preencher um nao preenche o outro — e quem digitou
                                    1000 aqui, achando que resolvia a nota, viu 1 metro entrar no
                                    estoque e o custo do rolo virar custo do metro. A tela nao dizia
                                    nada. Agora diz, e oferece a copia num clique.
                                    Fica FORA do <label> de proposito: dentro, o clique no botao
                                    tambem alternaria a caixa de marcacao. */}
                                {avisoDivergencia && (
                                  <div className="mt-1 px-1.5 py-1 rounded font-bold self-start"
                                    style={{ backgroundColor: '#fde68a', color: '#000', lineHeight: 1.35 }}>
                                    esta quantidade vale so no catalogo — o estoque desta nota entra
                                    por <b>{formatQtdBr(item.qtd_embalagem ?? 1)}</b>, que e o Qt.Emb da
                                    linha do item
                                    <button
                                      type="button"
                                      onClick={() => aplicarQtdDoCatalogoNoItem(idx)}
                                      className="ml-1.5 px-1.5 py-0.5 rounded border font-bold"
                                      style={{ borderColor: '#92400e', backgroundColor: '#fff', lineHeight: 1.2 }}
                                    >
                                      usar {formatQtdBr(parseQtdEmbNova(qtdDaLinhaNova(idx)))} tambem no item
                                    </button>
                                  </div>
                                )}
                              </td>
                              <td className={celula} colSpan={2}></td>
                              <td className={`${celula} font-mono`} style={{ fontWeight: marcada ? 700 : undefined }}>
                                {novo ? <span className="opacity-30">—</span> : (c!.codigo_fornecedor || <span className="opacity-30">sem codigo</span>)}
                              </td>
                              <td className={celula}>
                                {!novo && <span className={marcada ? '' : 'opacity-60'}>{c!.descricao_fornecedor}</span>}
                              </td>
                              <td className={celula}>
                                {/* O UNICO campo de marca da tela, e ele vive na LINHA — e a linha do
                                    catalogo que guarda a marca. Mesmo par do Catalogo: combo das
                                    marcas cadastradas + "+" pra cadastrar uma nova ([DA-600]). */}
                                {/* Na linha "criar uma linha nova" o campo so abre com a opcao MARCADA:
                                    sem isso ele oferece escolher a marca de uma linha que nao vai
                                    existir, e o valor ficaria escrito na tela sem destino. */}
                                {(() => {
                                  const travado = novo && !marcada;
                                  return (
                                    <div className="flex items-center gap-1" title={travado ? 'marque "criar uma linha nova para esta compra" pra definir a marca dela' : undefined}>
                                      <div style={{ flex: 1, minWidth: 0 }}>
                                        <ComboBox
                                          value={novo ? (marcaDaLinhaNova(idx) || null) : (marcaDaLinhaCatalogo(c!) || null)}
                                          onChange={v => editarMarca(idx, v == null ? '' : String(v), novo ? null : c!.id)}
                                          opcoes={opcoesMarca}
                                          placeholder="marca..."
                                          disabled={travado}
                                        />
                                      </div>
                                      <button
                                        type="button"
                                        disabled={travado}
                                        onClick={() => setMarcaNovaAlvo({ idx, idLinha: novo ? null : c!.id })}
                                        title="Cadastrar uma marca nova"
                                        className="rounded border px-1 py-0.5 disabled:opacity-40"
                                        style={{ borderColor: 'var(--input-border)', lineHeight: 1 }}
                                      >
                                        <Plus size={11} />
                                      </button>
                                    </div>
                                  );
                                })()}
                              </td>
                              <td className={celula} colSpan={3}></td>
                              {/* Os dois campos do modal "Editar item do catalogo" — Embalagem e
                                  Qtd na emb. — vivem AQUI, na linha que vai ser criada E na linha
                                  existente que vai receber a compra. A existente era so-leitura
                                  (quem a editava era o modal do Catalogo) ate 29/10/2026, quando
                                  ele pediu: *"o sistema nao permite alterar a unidade e a
                                  quantidade"* — com a importacao passando a GUARDAR a embalagem da
                                  linha, corrigir aqui e' corrigir de vez. Linha nao marcada segue
                                  so-leitura, pelo mesmo motivo da marca ([DA-600] p.6): campo
                                  aberto numa linha que nao recebe a compra convida a preencher o nada. */}
                              <td className={`${celula} text-center`}>
                                {!novo && marcada ? (
                                  <select
                                    value={edicaoPerna(idx)?.emb ?? pernaSemEdicao(idx, c!).emb}
                                    onChange={e => editarPerna(idx, c!.id, 'emb', e.target.value)}
                                    className="w-16 px-0.5 py-0.5 border rounded text-[10px]"
                                    style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--input-border)' }}
                                    title="Embalagem desta linha do catalogo — a importacao guarda e converte o preco da nota pra ela"
                                  >
                                    {/* A embalagem que a linha ja tem entra na lista mesmo fora do cadastro
                                        de unidades — senao o select abre vazio e salvar apagaria o dado. */}
                                    {[...new Set([pernaSemEdicao(idx, c!).emb, String(c!.embalagem || '').toUpperCase(), ...opcoesEmbalagemCat].filter(Boolean))]
                                      .map(s => <option key={s} value={s}>{s}</option>)}
                                  </select>
                                ) : novo ? (
                                  <select
                                    value={embDaLinhaNova(idx)}
                                    disabled={!marcada}
                                    onChange={e => editarCatalogoNovo(idx, '_cat_emb_nova', e.target.value)}
                                    className="w-16 px-0.5 py-0.5 border rounded text-[10px] disabled:opacity-40"
                                    style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--input-border)' }}
                                    title={marcada ? 'Embalagem com que esta linha nasce no catalogo' : 'marque "criar uma linha nova para esta compra" pra definir a embalagem dela'}
                                  >
                                    {opcoesEmbalagemCat.map(s => <option key={s} value={s}>{s}</option>)}
                                  </select>
                                ) : (
                                  c!.embalagem && <span className={marcada ? '' : 'opacity-60'}>{c!.embalagem}</span>
                                )}
                              </td>
                              <td className={`${celula} text-center`} style={{ fontWeight: marcada ? 700 : undefined }}>
                                {!novo && marcada ? (
                                  <input
                                    type="text"
                                    inputMode="decimal"
                                    value={edicaoPerna(idx)?.qtd ?? String(pernaSemEdicao(idx, c!).qtd).replace('.', ',')}
                                    onChange={e => editarPerna(idx, c!.id, 'qtd', e.target.value)}
                                    onBlur={e => editarPerna(idx, c!.id, 'qtd', String(parseQtdEmbNova(e.target.value)).replace('.', ','))}
                                    className="w-14 px-1 py-0.5 border rounded text-[10px] text-center font-bold"
                                    style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--input-border)' }}
                                    title="Quantos cabem em 1 embalagem desta linha — o preco da nota e convertido pra ela"
                                  />
                                ) : novo ? (
                                  <input
                                    type="text"
                                    inputMode="decimal"
                                    value={qtdDaLinhaNova(idx)}
                                    disabled={!marcada}
                                    onChange={e => editarCatalogoNovo(idx, '_cat_qtd_nova', e.target.value)}
                                    onBlur={e => editarCatalogoNovo(idx, '_cat_qtd_nova', String(parseQtdEmbNova(e.target.value)).replace('.', ','))}
                                    className="w-14 px-1 py-0.5 border rounded text-[10px] text-center font-bold disabled:opacity-40"
                                    style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--input-border)' }}
                                    title={marcada ? 'Quantos cabem em 1 embalagem — e o que divide o preco pra dar o unitario do catalogo' : 'marque "criar uma linha nova para esta compra" pra definir a quantidade dela'}
                                  />
                                ) : (
                                  c!.quantidade_embalagem != null
                                    ? String(c!.quantidade_embalagem).replace('.', ',')
                                    : ''
                                )}
                              </td>
                              {/* O PRECO DA EMBALAGEM que a linha vai gravar, com a embalagem colada
                                  (a coluna de cima e' "/un" — o numero sem rotulo afirmaria outra
                                  coisa). Na linha marcada e' a previa, pela mesma peca do servidor:
                                  1,241 por UN numa linha PCT 12 = 14,89/PCT. Era "preco da nota ÷
                                  Qtd ao lado", que tratava o preco do XML como o da embalagem da
                                  linha e dava o pacote de 12 a R$ 0,10 a unidade. Nas outras, o
                                  que a linha tem hoje. */}
                              <td className={`${celula} text-right whitespace-nowrap`} style={{ fontWeight: marcada ? 700 : undefined }}>
                                {(() => {
                                  if (novo && !marcada) return '';
                                  const emb = novo
                                    ? embDaLinhaNova(idx)
                                    : (marcada ? (edicaoPerna(idx)?.emb ?? pernaSemEdicao(idx, c!).emb) : c!.embalagem);
                                  const preco = novo
                                    ? precoEmbDaLinha(idx, emb, parseQtdEmbNova(qtdDaLinhaNova(idx)))
                                    : marcada
                                      ? (edicaoPerna(idx)
                                          ? precoEmbDaLinha(idx, emb, edicaoPerna(idx)!.qtd != null
                                              ? parseQtdEmbNova(edicaoPerna(idx)!.qtd!)
                                              : pernaSemEdicao(idx, c!).qtd)
                                          // sem edicao, o preco atual desempata — o mesmo que o servidor
                                          // faz; com a embalagem adotada nao ha o que desempatar
                                          : precoEmbDaLinha(idx, emb, pernaSemEdicao(idx, c!).qtd,
                                              pernaSemEdicao(idx, c!).adotada ? null : c!.preco_unitario))
                                      : c!.preco_unitario;
                                  if (preco == null) return '';
                                  const adotada = marcada && !novo && !edicaoPerna(idx) ? pernaSemEdicao(idx, c!).adotada : undefined;
                                  return (
                                    <span title={marcada ? 'Preco da embalagem que esta compra grava no catalogo' : 'Preco da embalagem que esta linha tem hoje'}>
                                      {formatMoedaSemSimbolo(preco)}{emb ? `/${String(emb).toUpperCase()}` : ''}
                                      {adotada && (
                                        <span className="block text-[10px] font-normal muted">
                                          {adotada === 'cadastro' ? 'embalagem do cadastro' : 'embalagem do outro CNPJ'}
                                        </span>
                                      )}
                                    </span>
                                  );
                                })()}
                              </td>
                              <td className={celula} colSpan={6}></td>
                            </tr>
                          );
                        });
                      })()}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {/* Fechamento do custo: confirma que a soma dos itens bate com o Total da entrada */}
            {(() => {
              const totalNF = (parseTotalNF() || 0) + parseOutrasDespesas();
              const somaXml = nfe?.valor_produtos || 0;
              const totalIPIAll = itensVinculados.reduce((s, it) => s + (parseFloat(String(it.valor_ipi)) || 0), 0);
              const custosComp = totalNF - somaXml - totalIPIAll;
              const fatorSh = (somaXml > 0 && custosComp > 0) ? custosComp / somaXml : 0;
              const somaCustos = itensVinculados.reduce((acc, it) => {
                const qtdEmb = it.qtd_embalagem ?? 1;
                const qtdTotal = it.quantidade * qtdEmb;
                if (qtdTotal <= 0) return acc;
                const vnf = it.valor_total;
                const od = it.valor_total * fatorSh;
                const ipi = parseFloat(String(it.valor_ipi)) || 0;
                const difal = calcDifal(it, nfe?.emitente?.endereco?.uf, nfe?.valor_frete || 0, nfe?.valor_produtos || 0);
                const st = stEmbutido ? 0 : (it.valor_icms_st || 0);
                const imposto = difal > 0 ? difal : st;
                return acc + vnf + od + ipi + imposto;
              }, 0);
              if (somaCustos <= 0) return null;
              const diff = Math.abs(somaCustos - totalNF);
              const bate = diff < 0.02;
              return (
                <div className="flex items-center justify-end gap-3 px-3 py-1.5 text-[11px] border-t" style={{ borderColor: 'var(--input-border)', backgroundColor: 'var(--grid-header-bg)', color: 'var(--grid-header-fg)' }}>
                  <span className="opacity-70">Custo total gravado nos produtos:</span>
                  <span className="font-bold">R$ {formatMoedaSemSimbolo(somaCustos)}</span>
                  {bate
                    ? <span className="text-green-700 font-semibold">= Total da entrada ✓</span>
                    : <span className="text-amber-700 font-semibold" title={`Diferença de R$ ${formatMoedaSemSimbolo(diff)} — pode haver DIFAL/ST não rateados`}>≠ Total da entrada (dif. R$ {formatMoedaSemSimbolo(diff)})</span>
                  }
                </div>
              );
            })()}
          </div>

          {/* Confirmar */}
          <div className="rounded-lg border p-4" style={{ borderColor: 'var(--input-border)', backgroundColor: 'var(--card-bg)' }}>
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                <input type="checkbox" checked={atualizarEstoque} onChange={e => setAtualizarEstoque(e.target.checked)} />
                Atualizar estoque e preco de custo dos produtos vinculados
              </label>
              <div className="flex items-center gap-3">
                {bloqueio && (
                  // Fundo solido + letra preta, nunca cor de texto ([DA-597]).
                  <span
                    className="flex items-center gap-2 font-bold text-xs px-2.5 py-1.5 rounded"
                    style={{ backgroundColor: '#fde68a', color: '#000' }}
                  >
                    <AlertTriangle size={14} /> {bloqueio.msg}
                    {bloqueio.alvo && (
                      <button onClick={() => irParaPendencia(bloqueio.alvo!)} className="underline font-bold" style={{ color: '#1d4ed8' }}>
                        ver o primeiro
                      </button>
                    )}
                  </span>
                )}
                <button
                  onClick={() => { setNfe(null); setItensVinculados([]); setFornecedorVinculado(null); setOutrasDespesas(''); }}
                  className="px-4 py-2 text-sm hover:bg-gray-100 rounded-lg transition"
                >
                  Cancelar
                </button>
                <button
                  onClick={confirmarImportacao}
                  disabled={salvando || !!bloqueio}
                  className="flex items-center gap-2 bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition text-sm disabled:opacity-50"
                >
                  {salvando ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
                  {salvando ? 'Importando...' : 'Confirmar Importacao'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL — CONSULTAR SEFAZ por chave (NFeDistribuicaoDFe AN) */}
      {modalConsultar && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="rounded-xl shadow-xl w-full max-w-lg" style={{ backgroundColor: 'var(--panel-bg)' }}>
            <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'var(--input-border)' }}>
              <h3 className="font-bold">Consultar NF-e na SEFAZ</h3>
              <button type="button" className="btn-fechar-modal" onClick={() => { setModalConsultar(false); setChaveConsulta(''); }} aria-label="Fechar">×</button>
            </div>
            <div className="p-5 space-y-3 text-sm">
              <div className="rounded-lg p-3 text-xs" style={{ backgroundColor: 'var(--accent-soft)', color: 'var(--page-fg)' }}>
                Cole a chave de acesso de 44 digitos. O sistema baixa o XML direto da SEFAZ
                e ja preenche os itens da nota. Funciona somente em notas onde a Fio de Cobre e a destinataria.
              </div>
              <div>
                <label className="block text-xs muted mb-1">Chave de Acesso</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={chaveConsulta}
                  onChange={e => setChaveConsulta(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !consultando) consultarPorChave({}); }}
                  placeholder="0000 0000 0000 0000 0000 0000 0000 0000 0000 0000 0000"
                  className="w-full px-3 py-2 border rounded-lg text-sm font-mono"
                  style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--input-border)' }}
                />
                <p className="text-xs muted mt-1">{chaveConsulta.replace(/\D/g, '').length}/44 digitos</p>
              </div>
            </div>
            <div className="flex justify-end gap-3 px-5 pb-4">
              <button onClick={() => { setModalConsultar(false); setChaveConsulta(''); }} className="px-4 py-2 text-sm hover:bg-gray-100 rounded-lg transition">
                Cancelar
              </button>
              <button
                onClick={() => consultarPorChave({})}
                disabled={consultando || chaveConsulta.replace(/\D/g, '').length !== 44}
                className="flex items-center gap-2 bg-emerald-600 text-white px-5 py-2 rounded-lg hover:bg-emerald-700 transition text-sm disabled:opacity-50"
              >
                {consultando ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
                {consultando ? 'Consultando SEFAZ...' : 'Consultar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL — CADASTRAR FORNECEDOR */}
      {modalFornecedor && nfe && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="rounded-xl shadow-xl w-full max-w-lg" style={{ backgroundColor: 'var(--panel-bg)' }}>
            <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'var(--input-border)' }}>
              <h3 className="font-bold">Cadastrar Fornecedor</h3>
              <button type="button" className="btn-fechar-modal" onClick={() => setModalFornecedor(false)} aria-label="Fechar">×</button>
            </div>
            <div className="p-5 space-y-3 text-sm">
              <p className="muted text-xs mb-3">Dados extraidos do XML. O fornecedor sera cadastrado automaticamente.</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <span className="text-xs muted block">Razao Social</span>
                  <span className="font-bold">{nfe.emitente.nome}</span>
                </div>
                <div>
                  <span className="text-xs muted block">CNPJ</span>
                  <span className="font-medium">{nfe.emitente.cnpj}</span>
                </div>
                <div>
                  <span className="text-xs muted block">IE</span>
                  <span className="font-medium">{nfe.emitente.ie || '—'}</span>
                </div>
                <div className="col-span-2">
                  <span className="text-xs muted block">Endereco</span>
                  <span className="text-xs">
                    {nfe.emitente.endereco.logradouro}, {nfe.emitente.endereco.numero} - {nfe.emitente.endereco.bairro}, {nfe.emitente.endereco.municipio}/{nfe.emitente.endereco.uf} — CEP {nfe.emitente.endereco.cep}
                  </span>
                </div>
                {nfe.emitente.endereco.fone && (
                  <div>
                    <span className="text-xs muted block">Telefone</span>
                    <span>{nfe.emitente.endereco.fone}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-3 px-5 pb-4">
              <button onClick={() => setModalFornecedor(false)} className="px-4 py-2 text-sm hover:bg-gray-100 rounded-lg transition">
                Cancelar
              </button>
              <button onClick={cadastrarFornecedor} disabled={salvandoFornecedor} className="flex items-center gap-2 bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700 transition text-sm disabled:opacity-50">
                {salvandoFornecedor ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
                {salvandoFornecedor ? 'Cadastrando...' : 'Cadastrar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL — VINCULAR FORNECEDOR */}
      {modalVincularForn && nfe && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="rounded-xl shadow-xl w-full max-w-lg" style={{ backgroundColor: 'var(--panel-bg)' }}>
            <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'var(--input-border)' }}>
              <h3 className="font-bold">Vincular Fornecedor Existente</h3>
              <button type="button" className="btn-fechar-modal" onClick={() => { setModalVincularForn(false); setBuscaForn(''); setResultadosForn([]); }} aria-label="Fechar">×</button>
            </div>
            <div className="p-5">
              <p className="muted text-xs mb-3">Busque pelo nome, razao social ou CNPJ. Os campos vazios sao preenchidos com dados do XML.</p>
              <div className="flex gap-2 mb-4">
                <input
                  type="text"
                  value={buscaForn}
                  onChange={e => setBuscaForn(upperMantendoCursor(e))}
                  onKeyDown={e => e.key === 'Enter' && buscarFornecedores()}
                  placeholder="NOME, RAZAO SOCIAL OU CNPJ..."
                  className="flex-1 px-3 py-2 border rounded-lg text-sm uppercase"
                  style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--input-border)' }}
                  autoFocus
                />
                <button onClick={buscarFornecedores} disabled={buscandoForn} className="flex items-center gap-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition text-sm disabled:opacity-50">
                  {buscandoForn ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
                  Buscar
                </button>
              </div>
              {resultadosForn.length > 0 && (
                <div className="border rounded-lg overflow-hidden max-h-60 overflow-y-auto" style={{ borderColor: 'var(--input-border)' }}>
                  {resultadosForn.map(f => (
                    <div
                      key={f.id}
                      className="flex items-center justify-between px-3 py-2.5 border-b last:border-b-0 hover:bg-blue-50 cursor-pointer transition"
                      onClick={() => vincularFornecedorExistente(f)}
                    >
                      <div>
                        <div className="font-medium text-sm">{f.nome_fantasia || f.razao_social}</div>
                        <div className="text-xs muted">{f.cpf_cnpj} {f.cidade && `— ${f.cidade}/${f.uf}`}</div>
                      </div>
                      <Link2 size={16} className="text-blue-500 flex-shrink-0" />
                    </div>
                  ))}
                </div>
              )}
              {resultadosForn.length === 0 && buscaForn && !buscandoForn && (
                <div className="text-center text-sm muted py-4">Nenhum fornecedor encontrado</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* MODAL — BUSCAR PRODUTO (LUPA) */}
      {modalBuscaProd && (
        <div
          ref={dragRef}
          className="fixed z-50 rounded-xl shadow-2xl border"
          style={{
            width: 520,
            left: modalPos.x || '50%',
            top: modalPos.y || '15%',
            transform: modalPos.x ? 'none' : 'translateX(-50%)',
            backgroundColor: 'var(--panel-bg)',
            borderColor: 'var(--input-border)',
          }}
        >
          <div className="flex items-center justify-between px-5 py-4 border-b cursor-move select-none" style={{ borderColor: 'var(--input-border)' }} onMouseDown={handleDragStart}>
            <h3 className="font-bold">Buscar Produto</h3>
            <button type="button" className="btn-fechar-modal" onClick={() => { setModalBuscaProd(null); setBuscaProd(''); setResultadosProd([]); setModalPos({ x: 0, y: 0 }); }} aria-label="Fechar">×</button>
          </div>
          <div className="p-5">
            <div className="flex gap-2 mb-4">
              <input
                ref={buscaProdInputRef}
                type="text"
                value={buscaProd}
                onChange={e => {
                  buscaProdSelRef.current = { start: e.target.selectionStart, end: e.target.selectionEnd };
                  setBuscaProd(upperMantendoCursor(e));
                }}
                onKeyDown={e => e.key === 'Enter' && buscarProdutos()}
                placeholder="Codigo, descricao ou EAN..."
                className="flex-1 px-3 py-2 border rounded-lg text-sm"
                style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--input-border)' }}
                autoFocus
              />
              <button onClick={buscarProdutos} disabled={buscandoProd} className="flex items-center gap-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition text-sm disabled:opacity-50">
                {buscandoProd ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
                Buscar
              </button>
            </div>
            {/* DA COTACAO DE ORIGEM (0356) — a lista curta do que foi cotado com este fornecedor, no
                topo porque e' ela que o operador escolheu ao informar a cotacao. Clicar faz o MESMO
                que escolher na busca (produto ou Novo Produto); item so de catalogo nao tem o que
                ligar e aparece apagado dizendo por que. */}
            {itensCotacao.length > 0 && (
              <div className="mb-3 border rounded-lg overflow-hidden" style={{ borderColor: 'var(--input-border)' }}>
                <div className="px-3 py-1 text-[10px] font-bold tracking-wide"
                  style={{ color: 'var(--text-muted)', backgroundColor: 'var(--grid-row-alt-bg)' }}>
                  DA COTACAO {cotacoesForn.find(c => c.id === cotacaoOrigemId)?.numero ?? ''} — O QUE VOCE PEDIU A ESTE FORNECEDOR
                </div>
                <div className="max-h-60 overflow-y-auto">
                  <table className="w-full text-sm">
                    <tbody>
                      {itensCotacao.map(c => {
                        const usadaEm = linhaQueUsaItemCotacao(c);
                        const escolhivel = !!(c.produto || c.novo_produto);
                        const foto = c.produto?.foto_url || c.novo_produto?.foto_url || null;
                        return (
                          <tr key={`cot-${c.cotacao_item_id}`}
                            className={`border-b last:border-b-0 ${escolhivel ? 'cursor-pointer hover:bg-black/5' : ''}`}
                            style={escolhivel ? undefined : { opacity: 0.55 }}
                            onClick={() => escolhivel && escolherItemCotacao(c)}
                            title={escolhivel ? 'Ligar esta linha da nota a este item' : (c.tipo === 'novo'
                              ? 'Este Novo Produto ja foi comprado (saiu da prateleira)'
                              : 'Item so do Catalogo do fornecedor, sem produto — use "+ Criar" na linha')}>
                            <td className="px-2 py-1.5 w-10">
                              {foto
                                ? <FotoProduto url={foto} tam={32} />
                                : <div style={{ width: 32, height: 32, borderRadius: 4, background: 'var(--grid-header-bg)' }} />}
                            </td>
                            <td className="px-2 py-1.5 whitespace-nowrap font-bold" style={c.tipo === 'novo' ? { color: 'var(--primary)' } : undefined}>
                              {c.produto?.codigo_interno ?? c.novo_produto?.codigo ?? '—'}
                            </td>
                            <td className="px-2 py-1.5">
                              <div>{c.produto && produtoInativo(c.produto) && <SeloInativo />}{c.descricao}</div>
                              <div className="text-[11px] muted">
                                {c.cod_fornecedor ? `Cod.F. ${c.cod_fornecedor} · ` : ''}Cotado: {c.cotado}
                              </div>
                            </td>
                            <td className="px-2 py-1.5 text-right whitespace-nowrap">
                              {c.tipo === 'novo' && c.novo_produto && (
                                <span style={{ backgroundColor: '#c4b5fd', color: '#000', padding: '1px 7px', borderRadius: 10, fontSize: 10, fontWeight: 700 }}>NOVO</span>
                              )}
                              {c.marcado_aqui && (
                                <span className="ml-1" title="Marcado no carrinho da cotacao como comprado deste fornecedor"
                                  style={{ backgroundColor: '#fde68a', color: '#000', padding: '1px 7px', borderRadius: 10, fontSize: 10, fontWeight: 700 }}>comprado aqui</span>
                              )}
                              {usadaEm != null && (
                                <div className="text-[10px] muted mt-0.5">ja na linha #{usadaEm}</div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {resultadosProd.length > 0 && (
              <div className="border rounded-lg overflow-hidden max-h-72 overflow-y-auto" style={{ borderColor: 'var(--input-border)' }}>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b sticky top-0" style={{ backgroundColor: 'var(--grid-header-bg)' }}>
                      <th className="text-center px-2 py-2 font-semibold w-12">Foto</th>
                      <th className="text-left px-3 py-2 font-semibold w-16">Codigo</th>
                      <th className="text-left px-3 py-2 font-semibold">Descricao</th>
                      <th className="text-right px-3 py-2 font-semibold w-20">Estoque</th>
                    </tr>
                  </thead>
                  <tbody>
                    {resultadosProd.map(p => (
                      <tr key={p.id} className="border-b last:border-b-0 hover:bg-blue-50 cursor-pointer transition" onClick={() => selecionarProdutoBusca(p)}>
                        <td className="px-2 py-1.5 text-center">
                          {p.foto_url ? (
                            <img src={p.foto_url} alt="" className="w-10 h-10 object-contain rounded mx-auto" />
                          ) : (
                            <div className="w-10 h-10 rounded flex items-center justify-center mx-auto" style={{ backgroundColor: 'var(--input-bg)' }}>
                              <Package size={16} className="opacity-30" />
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 codigo-curto">{p.codigo_interno}</td>
                        <td className="px-3 py-2">
                          <div>{produtoInativo(p) && <SeloInativo />}{p.descricao}</div>
                          {p.marca_nome && <div className="text-xs muted mt-0.5">{p.marca_nome}</div>}
                        </td>
                        <td className="px-3 py-2 text-right muted">{Number(p.estoque_atual || 0).toLocaleString('pt-BR')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* NOVOS PRODUTOS por ULTIMO — "primeiro pesquisa normal e por ultimo em Novos
                Produto" (regra do Ruan, 22/08/2026). Escolher aqui NAO vincula produto: marca
                qual item da prateleira esta nota aposenta ao ser salva. */}
            {resultadosNovos.length > 0 && (
              <div className="mt-3">
                <div className="px-3 py-1 text-[10px] font-bold tracking-wide"
                  style={{ color: 'var(--text-muted)', backgroundColor: 'var(--grid-row-alt-bg)' }}>
                  NOVOS PRODUTOS — A PRATELEIRA DO QUE VOCE IA COTAR
                </div>
                <table className="w-full text-sm">
                  <tbody>
                    {resultadosNovos.map(n => (
                      <tr key={`np-${n.id}`} className="cursor-pointer hover:bg-black/5"
                        onClick={() => selecionarNovoProdutoBusca(n)}>
                        <td className="px-3 py-2 w-12">
                          {n.foto_url
                            ? <FotoProduto url={n.foto_url} tam={32} />
                            : <div style={{ width: 32, height: 32, borderRadius: 4, background: 'var(--grid-header-bg)' }} />}
                        </td>
                        <td className="px-3 py-2 font-bold whitespace-nowrap" style={{ color: 'var(--primary)' }}>{n.codigo}</td>
                        <td className="px-3 py-2">
                          {n.descricao}
                          {n.marca && <span className="muted"> · {n.marca}</span>}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <span style={{ backgroundColor: '#c4b5fd', color: '#000', padding: '1px 7px', borderRadius: 10, fontSize: 10, fontWeight: 700 }}>NOVO</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {resultadosProd.length === 0 && resultadosNovos.length === 0 && buscaProd && !buscandoProd && (
              <div className="text-center text-sm muted py-4">Nenhum produto encontrado</div>
            )}
          </div>
        </div>
      )}

      {modalEmbCompra && (
        <ModalEmbalagemCompra
          produtoId={modalEmbCompra.produtoId}
          codigo={modalEmbCompra.codigo}
          descricao={modalEmbCompra.descricao}
          onFechar={() => setModalEmbCompra(null)}
          onGravado={(g) => {
            // Todas as linhas do mesmo produto: a previa do catalogo e a trava leem daqui.
            setItensVinculados(prev => prev.map(i => i.produto_id === modalEmbCompra.produtoId && i.produto_vinculado
              ? { ...i, produto_vinculado: { ...i.produto_vinculado, ...g } }
              : i));
            setModalEmbCompra(null);
          }}
        />
      )}

      {/* MODAL — CRIAR PRODUTO */}
      {modalProduto && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="rounded-xl shadow-xl w-full max-w-lg" style={{ backgroundColor: 'var(--panel-bg)' }}>
            <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'var(--input-border)' }}>
              <h3 className="font-bold">Criar Produto</h3>
              <button type="button" className="btn-fechar-modal" onClick={() => setModalProduto(null)} aria-label="Fechar">×</button>
            </div>
            <div className="p-5 space-y-3 text-sm">
              <p className="muted text-xs mb-3">Dados extraidos do XML. O produto sera criado com codigo interno automatico.</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <span className="text-xs muted block">Descricao</span>
                  <span className="font-bold">{modalProduto.item.descricao}</span>
                </div>
                <div>
                  <span className="text-xs muted block">EAN</span>
                  <span className="font-medium font-mono">{modalProduto.item.ean || '—'}</span>
                </div>
                <div>
                  <span className="text-xs muted block">Unidade (compra)</span>
                  <span className="font-medium">{modalProduto.item.unidade}</span>
                </div>
                {/* O UNICO campo editavel do modal, e o que faltava: sem ele o produto nascia
                    vendido na embalagem do fornecedor e sem Qtd na Emb. A frase derivada existe
                    porque o efeito e' invisivel na hora — quem cria o produto nao ve o estoque nem
                    o catalogo, que sao onde o erro aparece dias depois. */}
                <div className="col-span-2 rounded-lg border p-3" style={{ borderColor: '#fcd34d', backgroundColor: 'var(--input-bg)' }}>
                  <span className="text-xs muted block mb-1.5">Como este produto e VENDIDO</span>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs">1 <b>{modalProduto.item.unidade || 'UN'}</b> da nota =</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={novoQtdEmb}
                      onChange={e => setNovoQtdEmb(e.target.value)}
                      onBlur={e => setNovoQtdEmb(String(parseQtdEmbNova(e.target.value)).replace('.', ','))}
                      className="w-20 px-2 py-1 border rounded text-sm text-center font-bold"
                      style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--input-border)' }}
                      title="Quantos cabem em 1 embalagem da nota (ex: 100 metros num rolo)"
                    />
                    <select
                      value={String(novoUnVendaId || '')}
                      onChange={e => setNovoUnVendaId(e.target.value ? parseInt(e.target.value) : null)}
                      className="px-2 py-1 border rounded text-sm"
                      style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--input-border)' }}
                    >
                      {unidades.filter(u => u.ativo !== false || u.id === novoUnVendaId).map(u => (
                        <option key={u.id} value={String(u.id)}>{u.sigla} — {u.nome}{u.ativo === false ? ' (inativa)' : ''}</option>
                      ))}
                    </select>
                  </div>
                  {(() => {
                    const q = parseQtdEmbNova(novoQtdEmb);
                    const sigla = unidades.find(u => u.id === novoUnVendaId)?.sigla || 'UN';
                    const qtdNota = Number(modalProduto.item.quantidade) || 0;
                    const total = qtdNota * q;
                    const custo = q > 0 ? (Number(modalProduto.item.valor_unitario) || 0) / q : 0;
                    return (
                      <div className="mt-2 text-[11px] px-2 py-1.5 rounded" style={{ backgroundColor: '#fde68a', color: '#000' }}>
                        a nota traz <b>{qtdNota.toLocaleString('pt-BR')} {modalProduto.item.unidade || 'UN'}</b> →
                        entram <b>{total.toLocaleString('pt-BR', { maximumFractionDigits: 3 })} {sigla}</b> no
                        estoque · custo <b>{formatMoeda(custo)}</b>/{sigla}
                      </div>
                    );
                  })()}
                </div>
                {/* Como o produto e COMPRADO (regra dele, 30/10/2026) — e' o que a cotacao conta.
                    Nasce igual a nota; muda quando o fornecedor fatura por unidade e vende o pacote. */}
                <div className="col-span-2 rounded-lg border p-3" style={{ borderColor: 'var(--input-border)', backgroundColor: 'var(--input-bg)' }}>
                  <span className="text-xs muted block mb-1.5">Como este produto e COMPRADO</span>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs">Compra em</span>
                    <select
                      value={novoCompraUn ?? (modalProduto.item.unidade || '').toUpperCase()}
                      onChange={e => setNovoCompraUn(e.target.value)}
                      className="px-2 py-1 border rounded text-sm"
                      style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--input-border)' }}
                    >
                      {(() => {
                        const atual = novoCompraUn ?? (modalProduto.item.unidade || '').toUpperCase();
                        const lista = unidades.filter(u => u.ativo !== false || u.sigla === atual);
                        return (
                          <>
                            {atual && !lista.some(u => u.sigla === atual) && <option value={atual}>{atual} (nao cadastrada)</option>}
                            {lista.map(u => <option key={u.id} value={u.sigla}>{u.sigla} — {u.nome}{u.ativo === false ? ' (inativa)' : ''}</option>)}
                          </>
                        );
                      })()}
                    </select>
                    <span className="text-xs">com</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={novoCompraQtd ?? novoQtdEmb}
                      onChange={e => setNovoCompraQtd(e.target.value)}
                      onBlur={e => setNovoCompraQtd(String(parseQtdEmbNova(e.target.value)).replace('.', ','))}
                      className="w-20 px-2 py-1 border rounded text-sm text-center font-bold"
                      style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--input-border)' }}
                      title="Quantas unidades de venda vem na embalagem de compra (ex: pacote com 10)"
                    />
                    <span className="text-xs font-bold">{unidades.find(u => u.id === novoUnVendaId)?.sigla || 'UN'}</span>
                  </div>
                </div>
                <div>
                  <span className="text-xs muted block">NCM</span>
                  <span className="font-medium">{modalProduto.item.ncm || '—'}</span>
                </div>
                <div>
                  <span className="text-xs muted block">CEST</span>
                  <span className="font-medium">{modalProduto.item.cest || '—'}</span>
                </div>
                <div>
                  <span className="text-xs muted block">CFOP</span>
                  <span className="font-medium">{modalProduto.item.cfop || '—'}</span>
                </div>
                <div>
                  <span className="text-xs muted block">Preco de Custo</span>
                  <span className="font-bold">{formatMoeda(modalProduto.item.valor_unitario)}</span>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 px-5 pb-4">
              <button onClick={() => setModalProduto(null)} className="px-4 py-2 text-sm hover:bg-gray-100 rounded-lg transition">Cancelar</button>
              <button onClick={criarProduto} disabled={salvandoProduto} className="flex items-center gap-2 bg-green-600 text-white px-5 py-2 rounded-lg hover:bg-green-700 transition text-sm disabled:opacity-50">
                {salvandoProduto ? <Loader2 size={16} className="animate-spin" /> : <Package size={16} />}
                {salvandoProduto ? 'Criando...' : 'Criar Produto'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL — GERAR CONTAS A PAGAR (Onda 3.3c-CP) */}
      {modalGerarContas && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="rounded-2xl shadow-2xl w-full max-w-md" style={{ backgroundColor: 'var(--panel-bg)', position: 'relative' }}>
            <button type="button" className="btn-fechar-modal btn-fechar-modal--abs" onClick={() => setModalGerarContas(null)} aria-label="Fechar">×</button>
            <div className="p-6">
              <h3 className="text-lg font-semibold mb-2">Gerar contas a pagar</h3>
              <p className="text-sm muted mb-3">
                Esta nota tem <strong>{modalGerarContas.duplicatas.length}</strong> parcela(s). Cada parcela vira uma conta a pagar pendente.
              </p>
              {(() => {
                // Calculo identico ao backend pra preview no modal.
                // totalCpDesejado = vNF + outras - credito; rateado proporcional ao vDup do XML.
                const total = modalGerarContas.duplicatas.length;
                const somaDup = modalGerarContas.duplicatas.reduce((s, d) => s + Number(d.valor || 0), 0);
                const totalCpDesejado = modalGerarContas.valorTotalNF + modalGerarContas.outrasDespesas - modalGerarContas.creditoFornecedor;
                const totalCent = Math.round(totalCpDesejado * 100);
                let valoresCent: number[] = [];
                if (somaDup > 0) {
                  valoresCent = modalGerarContas.duplicatas.map(d => Math.round(totalCent * (Number(d.valor || 0) / somaDup)));
                  const sobra = totalCent - valoresCent.reduce((a, b) => a + b, 0);
                  valoresCent[total - 1] = (valoresCent[total - 1] ?? 0) + sobra;
                } else {
                  valoresCent = modalGerarContas.duplicatas.map(() => Math.floor(totalCent / total));
                }
                const mostrarAjuste = modalGerarContas.creditoFornecedor > 0 || modalGerarContas.outrasDespesas > 0;
                return (
                  <>
                    {mostrarAjuste && (
                      <div className="mb-2 text-[11px] rounded p-2" style={{ background: '#fef3c7', color: '#854d0e' }}>
                        Total NF: <strong>R$ {modalGerarContas.valorTotalNF.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</strong>
                        {modalGerarContas.creditoFornecedor > 0 && <> · Credito: <strong>− R$ {modalGerarContas.creditoFornecedor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</strong></>}
                        {modalGerarContas.outrasDespesas > 0 && <> · Outras: <strong>+ R$ {modalGerarContas.outrasDespesas.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</strong></>}
                        <> = Boleto: <strong>R$ {totalCpDesejado.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</strong></>
                      </div>
                    )}
                    <div className="rounded-lg border p-3 mb-4 max-h-40 overflow-y-auto" style={{ borderColor: 'var(--input-border)' }}>
                      {modalGerarContas.duplicatas.map((d, i) => {
                        const valorAjustado = (valoresCent[i] ?? 0) / 100;
                        const valorXml = Number(d.valor || 0);
                        const mudou = Math.abs(valorAjustado - valorXml) > 0.005;
                        return (
                          <div key={i} className="flex justify-between text-xs py-1">
                            <span>Parcela {d.numero || (i + 1)}</span>
                            <span>{d.vencimento}</span>
                            <span className="font-semibold">
                              {mudou && (
                                <span className="muted line-through mr-2 font-normal">
                                  R$ {valorXml.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                </span>
                              )}
                              R$ {valorAjustado.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </>
                );
              })()}
              <label className="block text-xs muted mb-1">Categoria de despesa *</label>
              <select
                value={categoriaContas}
                onChange={(e) => setCategoriaContas(e.target.value)}
                className="w-full mb-4"
                style={{ padding: 8 }}
              >
                <option value="">Selecione...</option>
                {categoriasDespesa.map((c) => (
                  <option key={c.id} value={c.id}>{c.nome}</option>
                ))}
              </select>
            </div>
            <div className="p-4 border-t flex gap-2" style={{ borderColor: 'var(--input-border)' }}>
              <button
                onClick={() => {
                  const notaId = modalGerarContas!.notaId;
                  setModalGerarContas(null);
                  setTimeout(() => navigate(`/notas-entrada/${notaId}/revisar-margens`), 200);
                }}
                disabled={gerandoContas}
                className="flex-1 px-4 py-2.5 rounded-lg transition"
                style={{ background: 'var(--card-bg)', color: 'var(--text-fg)' }}
              >
                Pular (so margens)
              </button>
              <button
                onClick={() => void gerarContasPagar()}
                disabled={gerandoContas || !categoriaContas}
                className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
              >
                {gerandoContas ? 'Gerando...' : 'Gerar contas'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cadastro rapido de marca, chamado pelo "+" de qualquer linha do catalogo. A marca criada
          volta JA ESCOLHIDA na linha que abriu o modal — senao o operador cadastra e tem que
          procurar de novo no combo. */}
      {marcaNovaAlvo && (
        <ModalCadastroRapido
          titulo="Nova Marca"
          endpoint="/api/marcas"
          campos={[{ key: 'nome', label: 'Nome', obrigatorio: true }]}
          onFechar={() => setMarcaNovaAlvo(null)}
          onSalvo={(novo: { id: number; nome: string }) => {
            setMarcas(prev => [...prev, novo].sort((a, b) => a.nome.localeCompare(b.nome)));
            if (marcaNovaAlvo.produto) editarMarcaProduto(marcaNovaAlvo.idx, novo.nome);
            else editarMarca(marcaNovaAlvo.idx, novo.nome, marcaNovaAlvo.idLinha);
            setMarcaNovaAlvo(null);
          }}
        />
      )}
    </div>
  );
}

// A coluna Localizacao ([DA-448]) — onde esta mercadoria vai ser guardada.
//
// Combo em TODAS as linhas de proposito, mesmo nas que o sistema ja sabe responder: campo que ora e'
// texto ora e' seletor obriga o operador a descobrir a regra antes de usar. Ele le o que ja veio
// preenchido e so mexe onde discorda.
//
// As opcoes sao **os lugares do proprio produto**, nao o cadastro inteiro (ajuste do Ruan, 05/08).
// A 1a versao oferecia as 137 prateleiras da loja em toda linha: escolher entre elas e' uma pergunta
// que o operador nao tem como responder olhando a nota — a mercadoria mora onde ela ja mora. Com a
// lista curta a coluna vira confirmacao, e a estrela ao lado do codigo diz qual e' a de venda.
//
// UNICA excecao, e ela existe porque a regra nao se aplica: produto SEM endereco nenhum nao tem
// lista propria pra mostrar. Ali o combo cai no cadastro inteiro — senao a celula nasce vazia
// justamente na linha em que ela e' a unica saida, e a mercadoria continua sem lugar.
const OUTRA_PRATELEIRA = '__outra_prateleira__';
const SO_DO_PRODUTO = '__so_do_produto__';

// "Cotado: 200 UN (2 CX)" — o que foi pedido ao fornecedor na cotacao de origem, ao lado do que a
// nota trouxe. E' a conferencia da embalagem que motivou a 0356: a nota em UN e a cotacao em CX
// mostram a conta do Qt.Emb sem ninguem precisar abrir a cotacao.
function CotadoDaLinha({ c }: { c: ItemCotacaoNota | null }) {
  if (!c) return null;
  return (
    <span className="text-[10px] w-fit" title="Quantidade pedida na cotacao de origem (caixas do cadastro, como saiu no pedido)">
      <strong>Cotado:</strong> {c.cotado}
    </span>
  );
}

function LocalDaEntrada({ value, opcoes, lugares, faltando, onChange }: {
  value: number | null;
  opcoes: OpcaoCombo[];
  lugares?: LugarProduto[];
  /** Sem prateleira escolhida — trava o Confirmar ([DA-618]). */
  faltando?: boolean;
  onChange: (v: number | null) => void;
}) {
  const atuais = lugares ?? [];
  const escolhido = atuais.find(l => l.localizacao_id === value);
  // A lista curta e' a do cadastro do produto ([DA-448], pedido dele); "Outra prateleira..." abre o
  // cadastro inteiro so pra linha que precisa (23/10/2026: a porca 1754 tem so T2 e chegou pra A).
  // Cadastrar a prateleira no produto e voltar nao serve: a sugestao so e' lida ao vincular, e numa
  // nota grande refazer a importacao custa caro. O backend ja aceita qualquer prateleira e
  // acrescenta o vinculo (`movimentarNoLugar`), sem tirar os que o produto tem.
  const [todas, setTodas] = useState(false);
  // Trocou o produto da linha: volta pra lista curta do produto novo.
  useEffect(() => { setTodas(false); }, [lugares]);
  const foraDoProduto = value != null && atuais.length > 0 && !escolhido;
  const mostrarTodas = atuais.length === 0 || todas || foraDoProduto;

  // Ordem preservada como veio do backend (principal, depois a ronda) — a mesma da ficha do produto.
  const opcoesDoProduto: OpcaoCombo[] = !mostrarTodas
    ? [
        ...atuais.map(l => ({ value: l.localizacao_id, label: l.principal ? `${l.codigo} ★` : l.codigo })),
        { value: OUTRA_PRATELEIRA, label: 'Outra prateleira...' },
      ]
    : atuais.length > 0
      ? [{ value: SO_DO_PRODUTO, label: '← só as deste produto' }, ...opcoes]
      : opcoes;

  // Aviso so quando mandar pra vitrine foi uma ESCOLHA — ou seja, havia outro lugar disponivel.
  // Produto de prateleira unica tem a estrela como unico destino possivel; acender laranja nele
  // pintaria 6 de cada 10 linhas e o sinal viraria papel de parede ([DA-410]).
  const naVitrineTendoAlternativa = !!escolhido?.principal && atuais.length > 1;

  const dica = atuais.length > 0
    ? `Lugares deste produto: ${atuais.map(l => `${l.codigo}${l.principal ? ' (vitrine)' : ''} = ${l.quantidade ?? '—'}`).join(' · ')}` +
      (foraDoProduto ? '\n\nPrateleira NOVA pra este produto — a nota a acrescenta no cadastro, junto das que ele ja tem.' : '')
    : 'Produto ainda sem endereco — escolher aqui ja o cadastra nesta prateleira.';

  return (
    // `outline` no wrapper, nao `border` no combo: a borda do `.combo-input` vem do CSS da casa e
    // uma classe Tailwind ali depende da ordem das folhas pra vencer — o contorno externo nao
    // disputa com ninguem e some junto com o wrapper.
    // Os dois sinais nunca coexistem NA MESMA celula (um exige valor, o outro exige vazio), mas
    // convivem na mesma COLUNA — e a mesma cor pros dois faria "atencao, vitrine" e "falta escolher"
    // lerem como a mesma coisa. Por isso o que separa e' a FORMA, nao a cor: solido pra atencao,
    // TRACEJADO pra pendencia ([DA-355] usou o mesmo recurso quando faltou cor disponivel).
    <div
      style={{
        width: 108,
        ...(faltando ? { outline: '2px dashed #dc2626', outlineOffset: 1, borderRadius: 4 } : {}),
        ...(naVitrineTendoAlternativa ? { outline: '2px solid #f97316', outlineOffset: 1, borderRadius: 4 } : {}),
      }}
      title={
        faltando
          ? `${dica}\n\nSem prateleira a nota nao entra: e daqui que o estoque sai se ela for desfeita.`
          : naVitrineTendoAlternativa
            ? `${dica}\n\nATENCAO: esta e a prateleira de VENDA (vitrine). Normalmente a mercadoria que chega vai pro deposito.`
            : dica
      }
    >
      <ComboBox
        opcoes={opcoesDoProduto}
        value={value ?? ''}
        onChange={(v) => {
          if (v === OUTRA_PRATELEIRA) { setTodas(true); return; }
          if (v === SO_DO_PRODUTO) { setTodas(false); if (foraDoProduto) onChange(null); return; }
          onChange(v === '' || v === null ? null : Number(v));
        }}
        // Instrucao, nunca um valor possivel da lista ([DA-608] p.3): o `—` de antes nao dizia o que
        // fazer, e agora esta celula e' obrigatoria.
        placeholder="escolha..."
      />
    </div>
  );
}
